import * as THREE from 'three';
import { GRAVITY, TERMINAL_VELOCITY } from '../constants.js';
import { BlockId } from '../world/BlockType.js';

// --- Entity system constants ---
const MAX_ENTITIES = 35;
const SPAWN_INTERVAL = 1.5;                // seconds between spawn attempts
const SPAWN_DIST_MIN = 20;                 // minimum spawn distance from player
const SPAWN_DIST_RANGE = 35;               // additional random spawn distance
const DESPAWN_DISTANCE = 85;               // remove entities farther than this
const VILLA_PROXIMITY_RADIUS = 40;         // blocks from villa center for context spawning

// Initial entity timers
const INITIAL_IDLE_MAX = 2;                // max initial idle seconds (random 0..N)
const INITIAL_SOUND_BASE = 3;              // base initial sound timer
const INITIAL_SOUND_RANGE = 8;             // random range for initial sound timer

// Wandering AI
const IDLE_CHANCE = 0.3;                   // chance to idle instead of wander
const IDLE_TIME_BASE = 1;                  // min idle seconds
const IDLE_TIME_RANGE = 4;                 // random range for idle seconds
const WANDER_TIME_BASE = 1;               // min wander seconds
const WANDER_TIME_RANGE = 3;              // random range for wander seconds
const VILLAGER_HOME_RADIUS = 15;           // max distance before villager turns home
const WOLF_WILD_SPEED_FACTOR = 0.5;        // wild wolves wander at half speed

// Wolf AI
const WOLF_TELEPORT_DISTANCE = 30;         // tamed wolf teleports if farther
const WOLF_TELEPORT_OFFSET = 3;            // teleport radius around player
const WOLF_FOLLOW_DISTANCE = 4;            // tamed wolf follows if farther
const WOLF_AGGRO_LOSS_DISTANCE = 40;       // hostile wolf loses aggro beyond this
const WOLF_MELEE_RANGE = 2.0;              // attack reach in blocks
const WOLF_ATTACK_COOLDOWN = 1.0;          // seconds between wolf attacks
const WOLF_ATTACK_DAMAGE = 4;              // damage per wolf attack
const WOLF_TAME_CHANCE = 0.33;             // bone-tame success probability
const WOLF_TAMED_HEALTH = 20;              // health when tamed

// Herobrine
const HEROBRINE_FIRST_TIMER_BASE = 60;     // min seconds before first Herobrine spawn
const HEROBRINE_FIRST_TIMER_RANGE = 120;   // random range added to first timer
const HEROBRINE_RESPAWN_BASE = 90;         // min seconds between Herobrine respawns
const HEROBRINE_RESPAWN_RANGE = 180;       // random range for respawn timer
const HEROBRINE_SPAWN_DIST_BASE = 40;      // minimum spawn distance
const HEROBRINE_SPAWN_DIST_RANGE = 20;     // random additional spawn distance
const HEROBRINE_LIFETIME_BASE = 8;         // min seconds Herobrine stays
const HEROBRINE_LIFETIME_RANGE = 6;        // random additional lifetime
const HEROBRINE_VANISH_DISTANCE = 15;      // vanishes if player gets closer
const HEROBRINE_DESPAWN_DISTANCE = 100;    // despawn if too far
const HEROBRINE_SOUND_BASE = 3;            // base seconds between eerie sounds
const HEROBRINE_SOUND_RANGE = 3;           // random range for sound interval
const HEROBRINE_INITIAL_SOUND_DELAY = 2;   // seconds before first sound

// Animation
const WALK_ANIM_SPEED = 8;
const WALK_ANIM_AMPLITUDE = 0.5;
const ARM_SWING_FACTOR = 0.5;
const HEAD_BOB_AMPLITUDE = 0.03;
const HEAD_BOB_FREQ_FACTOR = 2;

// Physics
const GROUND_CHECK_OFFSET = 0.05;          // epsilon below entity to check ground
const WORLD_BOTTOM = 0;                    // minimum Y position

// --- Object pool: reusable temp vectors for hot paths (AI, physics) ---
const _tempVec3A = new THREE.Vector3();
const _tempVec3B = new THREE.Vector3();
const _tempVec3C = new THREE.Vector3();

class Entity {
  constructor(type, x, y, z) {
    this.type = type;
    this.position = new THREE.Vector3(x, y, z);
    this.velocity = new THREE.Vector3(0, 0, 0);
    this.mesh = null;
    this.wanderTimer = 0;
    this.wanderDir = new THREE.Vector3(0, 0, 0);
    this.idleTimer = Math.random() * INITIAL_IDLE_MAX;
    this.homePosition = new THREE.Vector3(x, y, z);
    this.animTime = Math.random() * 10;
    this.variant = null;
    this.health = 10;
    this.maxHealth = 10;
    // Animation references
    this.legFL = null; this.legFR = null;
    this.legBL = null; this.legBR = null;
    this.armL = null; this.armR = null;
    this.headMesh = null;
    // Sound timer: random interval between sounds
    this.soundTimer = INITIAL_SOUND_BASE + Math.random() * INITIAL_SOUND_RANGE;
    // Education (villagers only)
    this.education = null;
    // Name tag sprite
    this.nameTag = null;
    // Wolf-specific properties
    this.tamed = false;
    this.owner = null;        // reserved for taming owner reference
    this.wolfHostile = false;  // true when wolf retaliates after being attacked
    this.wolfTarget = null;    // attack target position (for hostile mode)
    this.wolfAttackCooldown = 0;
    // Blob shadow
    this.shadow = null;
  }
}

// Sound intervals per mob type (min, max seconds between sounds)
const SOUND_INTERVALS = {
  PIG: [4, 12],
  SHEEP: [5, 15],
  COW: [6, 18],
  CHICKEN: [3, 8],
  WOLF: [5, 12],
  VILLAGER: [4, 10],
  STEVE: [999, 999],   // Silent
  ALEX: [999, 999],    // Silent
  HEROBRINE: [3, 6],   // Handled separately
  HORSE: [5, 12],
  CAT: [4, 10],
  BEE: [2, 5],
  CAR: [999, 999],
  MOTO: [999, 999],
};

// Minecraft pixel scale: 1 pixel = 1/16 block
const PX = 1 / 16;

// Create a box with per-face colors (Minecraft style - no textures, colored faces)
function mcBox(w, h, d, colors) {
  // colors: { top, bottom, front, back, left, right } or single color
  const geo = new THREE.BoxGeometry(w, h, d);
  if (typeof colors === 'number') {
    return new THREE.Mesh(geo, new THREE.MeshLambertMaterial({ color: colors }));
  }
  const mats = [
    new THREE.MeshLambertMaterial({ color: colors.right || colors.side || 0x888888 }),
    new THREE.MeshLambertMaterial({ color: colors.left || colors.side || 0x888888 }),
    new THREE.MeshLambertMaterial({ color: colors.top || 0x888888 }),
    new THREE.MeshLambertMaterial({ color: colors.bottom || colors.top || 0x888888 }),
    new THREE.MeshLambertMaterial({ color: colors.front || colors.side || 0x888888 }),
    new THREE.MeshLambertMaterial({ color: colors.back || colors.side || 0x888888 }),
  ];
  return new THREE.Mesh(geo, mats);
}

function simpleBox(w, h, d, color) {
  const m = new THREE.Mesh(
    new THREE.BoxGeometry(w, h, d),
    new THREE.MeshLambertMaterial({ color })
  );
  /* Todos los mobs proyectan sombra. Va aquí, en la única fábrica de cajas,
     en vez de repetirlo en las dieciséis criaturas — y así el que venga
     después la hereda sin acordarse. */
  m.castShadow = true;
  return m;
}

// ==================== PIG ====================
// Minecraft pig: 8x8x8 head, 8x16x10 body (pixel coords)
// Scaled to game units: body ~1 block long
function buildPig(group) {
  const S = PX * 1.8; // scale factor

  // Body (10w x 8h x 16d pixels)
  const body = simpleBox(10*S, 8*S, 16*S, 0xE8A0A0);
  body.position.set(0, 8*S, 0);
  group.add(body);

  // Head (8x8x8)
  const headGroup = new THREE.Group();
  const head = simpleBox(8*S, 8*S, 8*S, 0xE8A0A0);
  headGroup.add(head);

  // Snout (4w x 3h x 1d) - protruding
  const snout = simpleBox(4*S, 3*S, 1*S, 0xE88888);
  snout.position.set(0, -1*S, 4.5*S);
  headGroup.add(snout);

  // Nostrils on snout
  const nostrilL = simpleBox(1*S, 1*S, 0.5*S, 0x4A2020);
  nostrilL.position.set(-1*S, -1*S, 5.2*S);
  headGroup.add(nostrilL);
  const nostrilR = simpleBox(1*S, 1*S, 0.5*S, 0x4A2020);
  nostrilR.position.set(1*S, -1*S, 5.2*S);
  headGroup.add(nostrilR);

  // Eyes (white + pupil)
  const eyeWhiteL = simpleBox(2*S, 2*S, 0.5*S, 0xFFFFFF);
  eyeWhiteL.position.set(-2*S, 1*S, 4.2*S);
  headGroup.add(eyeWhiteL);
  const eyeWhiteR = simpleBox(2*S, 2*S, 0.5*S, 0xFFFFFF);
  eyeWhiteR.position.set(2*S, 1*S, 4.2*S);
  headGroup.add(eyeWhiteR);
  const pupilL = simpleBox(1*S, 1*S, 0.5*S, 0x222222);
  pupilL.position.set(-1.5*S, 1*S, 4.5*S);
  headGroup.add(pupilL);
  const pupilR = simpleBox(1*S, 1*S, 0.5*S, 0x222222);
  pupilR.position.set(2.5*S, 1*S, 4.5*S);
  headGroup.add(pupilR);

  // Ears
  const earL = simpleBox(2*S, 3*S, 1*S, 0xD89090);
  earL.position.set(-3.5*S, 4*S, -1*S);
  earL.rotation.z = -0.3;
  headGroup.add(earL);
  const earR = simpleBox(2*S, 3*S, 1*S, 0xD89090);
  earR.position.set(3.5*S, 4*S, -1*S);
  earR.rotation.z = 0.3;
  headGroup.add(earR);

  headGroup.position.set(0, 12*S, 10*S);
  group.add(headGroup);

  // Legs (4x6x4 each)
  const legH = 6*S;
  const legW = 4*S;
  const legColor = 0xE8A0A0;

  const legFL = new THREE.Group();
  const legFLmesh = simpleBox(legW, legH, legW, legColor);
  legFLmesh.position.set(0, -legH/2, 0);
  legFL.add(legFLmesh);
  legFL.position.set(-3*S, 4*S + legH/2, 5*S);
  group.add(legFL);

  const legFR = new THREE.Group();
  const legFRmesh = simpleBox(legW, legH, legW, legColor);
  legFRmesh.position.set(0, -legH/2, 0);
  legFR.add(legFRmesh);
  legFR.position.set(3*S, 4*S + legH/2, 5*S);
  group.add(legFR);

  const legBL = new THREE.Group();
  const legBLmesh = simpleBox(legW, legH, legW, legColor);
  legBLmesh.position.set(0, -legH/2, 0);
  legBL.add(legBLmesh);
  legBL.position.set(-3*S, 4*S + legH/2, -5*S);
  group.add(legBL);

  const legBR = new THREE.Group();
  const legBRmesh = simpleBox(legW, legH, legW, legColor);
  legBRmesh.position.set(0, -legH/2, 0);
  legBR.add(legBRmesh);
  legBR.position.set(3*S, 4*S + legH/2, -5*S);
  group.add(legBR);

  // Tail (curly)
  const tail = simpleBox(1*S, 1*S, 4*S, 0xE8A0A0);
  tail.position.set(0, 10*S, -9*S);
  tail.rotation.x = 0.6;
  group.add(tail);

  return { headMesh: headGroup, legFL, legFR, legBL, legBR };
}

// ==================== SHEEP ====================
function buildSheep(group) {
  const S = PX * 1.8;

  // Wool body (outer, fluffy - slightly larger than inner)
  const wool = simpleBox(12*S, 10*S, 18*S, 0xF0F0F0);
  wool.position.set(0, 9*S, 0);
  group.add(wool);

  // Head (darker, no wool, 6x6x8)
  const headGroup = new THREE.Group();
  const head = simpleBox(6*S, 6*S, 8*S, 0xA0A0A0);
  headGroup.add(head);

  // Eyes
  const eyeL = simpleBox(1.5*S, 1.5*S, 0.5*S, 0xFFFFFF);
  eyeL.position.set(-1.5*S, 1*S, 4.2*S);
  headGroup.add(eyeL);
  const eyeR = simpleBox(1.5*S, 1.5*S, 0.5*S, 0xFFFFFF);
  eyeR.position.set(1.5*S, 1*S, 4.2*S);
  headGroup.add(eyeR);
  const pupilL = simpleBox(0.8*S, 1.5*S, 0.5*S, 0x222222);
  pupilL.position.set(-1.2*S, 1*S, 4.5*S);
  headGroup.add(pupilL);
  const pupilR = simpleBox(0.8*S, 1.5*S, 0.5*S, 0x222222);
  pupilR.position.set(1.8*S, 1*S, 4.5*S);
  headGroup.add(pupilR);

  // Mouth
  const mouth = simpleBox(3*S, 0.5*S, 0.5*S, 0x666666);
  mouth.position.set(0, -2*S, 4.2*S);
  headGroup.add(mouth);

  // Ears (horizontal)
  const earL = simpleBox(3*S, 1.5*S, 1*S, 0xA0A0A0);
  earL.position.set(-4.5*S, 1*S, -1*S);
  earL.rotation.z = -0.3;
  headGroup.add(earL);
  const earR = simpleBox(3*S, 1.5*S, 1*S, 0xA0A0A0);
  earR.position.set(4.5*S, 1*S, -1*S);
  earR.rotation.z = 0.3;
  headGroup.add(earR);

  headGroup.position.set(0, 14*S, 12*S);
  group.add(headGroup);

  // Legs (3x6x3)
  const legH = 6*S;
  const legW = 3*S;
  const legColor = 0x888888;

  const legFL = new THREE.Group();
  legFL.add(simpleBox(legW, legH, legW, legColor));
  legFL.children[0].position.y = -legH/2;
  legFL.position.set(-4*S, 4*S + legH/2, 6*S);
  group.add(legFL);

  const legFR = new THREE.Group();
  legFR.add(simpleBox(legW, legH, legW, legColor));
  legFR.children[0].position.y = -legH/2;
  legFR.position.set(4*S, 4*S + legH/2, 6*S);
  group.add(legFR);

  const legBL = new THREE.Group();
  legBL.add(simpleBox(legW, legH, legW, legColor));
  legBL.children[0].position.y = -legH/2;
  legBL.position.set(-4*S, 4*S + legH/2, -6*S);
  group.add(legBL);

  const legBR = new THREE.Group();
  legBR.add(simpleBox(legW, legH, legW, legColor));
  legBR.children[0].position.y = -legH/2;
  legBR.position.set(4*S, 4*S + legH/2, -6*S);
  group.add(legBR);

  return { headMesh: headGroup, legFL, legFR, legBL, legBR };
}

// ==================== COW ====================
function buildCow(group) {
  const S = PX * 1.8;

  // Body (brown, 12w x 10h x 18d)
  const body = simpleBox(12*S, 10*S, 18*S, 0x443322);
  body.position.set(0, 10*S, 0);
  group.add(body);

  // White belly patch
  const belly = simpleBox(10*S, 4*S, 14*S, 0xF4F4F4);
  belly.position.set(0, 6*S, 0);
  group.add(belly);

  // White patches on body
  const patch1 = simpleBox(4*S, 4*S, 6*S, 0xF4F4F4);
  patch1.position.set(3*S, 13*S, 3*S);
  group.add(patch1);
  const patch2 = simpleBox(5*S, 3*S, 5*S, 0xF4F4F4);
  patch2.position.set(-2*S, 12*S, -4*S);
  group.add(patch2);

  // Head (8x8x6)
  const headGroup = new THREE.Group();
  const head = simpleBox(8*S, 8*S, 6*S, 0x443322);
  headGroup.add(head);

  // Muzzle (lighter)
  const muzzle = simpleBox(6*S, 4*S, 2*S, 0xB8A898);
  muzzle.position.set(0, -1*S, 4*S);
  headGroup.add(muzzle);

  // Nostrils
  const nostrilL = simpleBox(1*S, 1*S, 0.5*S, 0x332211);
  nostrilL.position.set(-1.5*S, -1*S, 5.2*S);
  headGroup.add(nostrilL);
  const nostrilR = simpleBox(1*S, 1*S, 0.5*S, 0x332211);
  nostrilR.position.set(1.5*S, -1*S, 5.2*S);
  headGroup.add(nostrilR);

  // Eyes
  const eyeL = simpleBox(2*S, 2*S, 0.5*S, 0xFFFFFF);
  eyeL.position.set(-2*S, 2*S, 3.2*S);
  headGroup.add(eyeL);
  const eyeR = simpleBox(2*S, 2*S, 0.5*S, 0xFFFFFF);
  eyeR.position.set(2*S, 2*S, 3.2*S);
  headGroup.add(eyeR);
  const pupilL = simpleBox(1*S, 1.5*S, 0.5*S, 0x222222);
  pupilL.position.set(-1.5*S, 2*S, 3.5*S);
  headGroup.add(pupilL);
  const pupilR = simpleBox(1*S, 1.5*S, 0.5*S, 0x222222);
  pupilR.position.set(2.5*S, 2*S, 3.5*S);
  headGroup.add(pupilR);

  // Horns
  const hornL = simpleBox(1*S, 3*S, 1*S, 0xEEDDCC);
  hornL.position.set(-3.5*S, 5*S, 0);
  hornL.rotation.z = -0.4;
  headGroup.add(hornL);
  const hornR = simpleBox(1*S, 3*S, 1*S, 0xEEDDCC);
  hornR.position.set(3.5*S, 5*S, 0);
  hornR.rotation.z = 0.4;
  headGroup.add(hornR);

  // Ears
  const earL = simpleBox(3*S, 2*S, 1*S, 0x443322);
  earL.position.set(-5*S, 2*S, 0);
  headGroup.add(earL);
  const earR = simpleBox(3*S, 2*S, 1*S, 0x443322);
  earR.position.set(5*S, 2*S, 0);
  headGroup.add(earR);

  headGroup.position.set(0, 14*S, 12*S);
  group.add(headGroup);

  // Legs (4x12x4 - taller than pig)
  const legH = 8*S;
  const legW = 4*S;
  const legColor = 0x443322;

  const legFL = new THREE.Group();
  legFL.add(simpleBox(legW, legH, legW, legColor));
  legFL.children[0].position.y = -legH/2;
  legFL.position.set(-4*S, 5*S + legH/2, 6*S);
  group.add(legFL);

  const legFR = new THREE.Group();
  legFR.add(simpleBox(legW, legH, legW, legColor));
  legFR.children[0].position.y = -legH/2;
  legFR.position.set(4*S, 5*S + legH/2, 6*S);
  group.add(legFR);

  const legBL = new THREE.Group();
  legBL.add(simpleBox(legW, legH, legW, legColor));
  legBL.children[0].position.y = -legH/2;
  legBL.position.set(-4*S, 5*S + legH/2, -6*S);
  group.add(legBL);

  const legBR = new THREE.Group();
  legBR.add(simpleBox(legW, legH, legW, legColor));
  legBR.children[0].position.y = -legH/2;
  legBR.position.set(4*S, 5*S + legH/2, -6*S);
  group.add(legBR);

  // Udder
  const udder = simpleBox(4*S, 2*S, 4*S, 0xFFAAAA);
  udder.position.set(0, 5*S, -3*S);
  group.add(udder);

  // Tail
  const tail = simpleBox(1*S, 6*S, 1*S, 0x443322);
  tail.position.set(0, 12*S, -10*S);
  tail.rotation.x = -0.3;
  group.add(tail);

  return { headMesh: headGroup, legFL, legFR, legBL, legBR };
}

// ==================== CHICKEN ====================
function buildChicken(group) {
  const S = PX * 1.6;

  // Body (6w x 6h x 8d)
  const body = simpleBox(6*S, 6*S, 8*S, 0xF0F0F0);
  body.position.set(0, 6*S, 0);
  group.add(body);

  // Head (4x6x3)
  const headGroup = new THREE.Group();
  const head = simpleBox(4*S, 6*S, 3*S, 0xF0F0F0);
  headGroup.add(head);

  // Beak (orange, protruding)
  const beak = simpleBox(2*S, 1*S, 2*S, 0xEE8800);
  beak.position.set(0, -0.5*S, 2.5*S);
  headGroup.add(beak);

  // Comb (red on top)
  const comb = simpleBox(1.5*S, 2*S, 2*S, 0xCC0000);
  comb.position.set(0, 4*S, 0);
  headGroup.add(comb);

  // Wattle (red under beak)
  const wattle = simpleBox(1*S, 2*S, 1*S, 0xCC0000);
  wattle.position.set(0, -2.5*S, 1.5*S);
  headGroup.add(wattle);

  // Eyes
  const eyeL = simpleBox(0.8*S, 0.8*S, 0.5*S, 0x222222);
  eyeL.position.set(-1.5*S, 1*S, 1.7*S);
  headGroup.add(eyeL);
  const eyeR = simpleBox(0.8*S, 0.8*S, 0.5*S, 0x222222);
  eyeR.position.set(1.5*S, 1*S, 1.7*S);
  headGroup.add(eyeR);

  headGroup.position.set(0, 12*S, 5*S);
  group.add(headGroup);

  // Wings
  const wingL = simpleBox(1*S, 4*S, 6*S, 0xE8E8E8);
  wingL.position.set(-3.8*S, 7*S, 0);
  group.add(wingL);
  const wingR = simpleBox(1*S, 4*S, 6*S, 0xE8E8E8);
  wingR.position.set(3.8*S, 7*S, 0);
  group.add(wingR);

  // Tail feathers (angled up)
  const tail = simpleBox(3*S, 4*S, 2*S, 0xE0E0E0);
  tail.position.set(0, 9*S, -5*S);
  tail.rotation.x = -0.5;
  group.add(tail);

  // Legs (thin, yellow-orange)
  const legFL = new THREE.Group();
  const legLmesh = simpleBox(1*S, 5*S, 1*S, 0xCC8800);
  legLmesh.position.y = -2.5*S;
  legFL.add(legLmesh);
  // Foot
  const footL = simpleBox(2*S, 0.5*S, 3*S, 0xCC8800);
  footL.position.set(0, -5*S, 0.5*S);
  legFL.add(footL);
  legFL.position.set(-2*S, 3*S + 2.5*S, 0);
  group.add(legFL);

  const legFR = new THREE.Group();
  const legRmesh = simpleBox(1*S, 5*S, 1*S, 0xCC8800);
  legRmesh.position.y = -2.5*S;
  legFR.add(legRmesh);
  const footR = simpleBox(2*S, 0.5*S, 3*S, 0xCC8800);
  footR.position.set(0, -5*S, 0.5*S);
  legFR.add(footR);
  legFR.position.set(2*S, 3*S + 2.5*S, 0);
  group.add(legFR);

  return { headMesh: headGroup, legFL, legFR, legBL: null, legBR: null };
}

// ==================== VILLAGER ====================
function buildVillager(group, variant, profession) {
  const S = PX * 1.8;

  // Colors based on variant
  let robeColor = 0x8B6B3E;
  let robeColor2 = 0x7A5A2E;
  let skinColor = 0xCCA88A;
  let noseColor = 0xBB9970;

  if (variant === 'poor') {
    robeColor = 0x5A4A3A;
    robeColor2 = 0x3A2A1A;
    skinColor = 0xBB9878;
  } else if (variant === 'rich') {
    robeColor = 0x6A2A6A;
    robeColor2 = 0x4A1A4A;
  }

  // Profession overrides robe color
  const profData = profession ? PROFESSIONS[profession] : null;
  if (profData) {
    robeColor = profData.robeColor;
    robeColor2 = profData.robeColor;
  }

  // Body/Torso (8w x 12h x 4d)
  const torso = simpleBox(8*S, 12*S, 4*S, robeColor);
  torso.position.set(0, 18*S, 0);
  group.add(torso);

  // Robe skirt (wider bottom, 10w x 6h x 6d)
  const skirt = simpleBox(10*S, 6*S, 6*S, robeColor2);
  skirt.position.set(0, 9*S, 0);
  group.add(skirt);

  // Head (8x10x8 - tall Minecraft villager head)
  const headGroup = new THREE.Group();
  const head = simpleBox(8*S, 10*S, 8*S, skinColor);
  headGroup.add(head);

  // BIG NOSE (iconic villager nose - 2w x 4h x 2d)
  const nose = simpleBox(2*S, 4*S, 3*S, noseColor);
  nose.position.set(0, -1*S, 5.5*S);
  headGroup.add(nose);

  // Eyes (green Minecraft villager eyes)
  const eyeL = simpleBox(2*S, 2*S, 0.5*S, 0xFFFFFF);
  eyeL.position.set(-2*S, 2*S, 4.2*S);
  headGroup.add(eyeL);
  const eyeR = simpleBox(2*S, 2*S, 0.5*S, 0xFFFFFF);
  eyeR.position.set(2*S, 2*S, 4.2*S);
  headGroup.add(eyeR);
  // Green iris
  const irisL = simpleBox(1*S, 1.5*S, 0.5*S, 0x3A7A3A);
  irisL.position.set(-1.5*S, 2*S, 4.5*S);
  headGroup.add(irisL);
  const irisR = simpleBox(1*S, 1.5*S, 0.5*S, 0x3A7A3A);
  irisR.position.set(2.5*S, 2*S, 4.5*S);
  headGroup.add(irisR);

  // Unibrow (angry/serious villager look)
  const brow = simpleBox(6*S, 1*S, 0.5*S, 0x5A4A3A);
  brow.position.set(0, 4*S, 4.2*S);
  headGroup.add(brow);

  headGroup.position.set(0, 30*S, 0);
  group.add(headGroup);

  // Arms (4w x 12h x 4d, crossed in front - Minecraft villager style)
  const armL = new THREE.Group();
  const armLmesh = simpleBox(4*S, 12*S, 4*S, robeColor);
  armLmesh.position.y = -6*S;
  armL.add(armLmesh);
  // Hand
  const handL = simpleBox(3*S, 2*S, 3*S, skinColor);
  handL.position.set(0, -12*S, 1*S);
  armL.add(handL);
  armL.position.set(-6*S, 24*S, 0);
  group.add(armL);

  const armR = new THREE.Group();
  const armRmesh = simpleBox(4*S, 12*S, 4*S, robeColor);
  armRmesh.position.y = -6*S;
  armR.add(armRmesh);
  const handR = simpleBox(3*S, 2*S, 3*S, skinColor);
  handR.position.set(0, -12*S, 1*S);
  armR.add(handR);
  armR.position.set(6*S, 24*S, 0);
  group.add(armR);

  // Feet (under the robe)
  const footL = simpleBox(3*S, 2*S, 4*S, 0x444444);
  footL.position.set(-2.5*S, 1*S, 1*S);
  group.add(footL);
  const footR = simpleBox(3*S, 2*S, 4*S, 0x444444);
  footR.position.set(2.5*S, 1*S, 1*S);
  group.add(footR);

  // Hat/headwear
  if (profData) {
    // Profession-specific hat
    if (profession === 'builder') {
      // Hard hat (rounded yellow)
      const hardhat = simpleBox(11*S, 4*S, 11*S, profData.hatColor);
      hardhat.position.set(0, 37*S, 0);
      group.add(hardhat);
      const brim = simpleBox(13*S, 1*S, 13*S, profData.hatColor);
      brim.position.set(0, 35.5*S, 0);
      group.add(brim);
    } else if (profession === 'doctor' || profession === 'vet') {
      // Medical headband + small cap
      const cap = simpleBox(9*S, 2*S, 9*S, profData.hatColor);
      cap.position.set(0, 36.5*S, 0);
      group.add(cap);
      // Red/green cross on cap
      const crossColor = profession === 'doctor' ? 0xCC0000 : 0x00AA00;
      const crossV = simpleBox(1*S, 3*S, 0.5*S, crossColor);
      crossV.position.set(0, 36.5*S, 4.6*S);
      group.add(crossV);
      const crossH = simpleBox(3*S, 1*S, 0.5*S, crossColor);
      crossH.position.set(0, 36.5*S, 4.6*S);
      group.add(crossH);
    } else if (profession === 'scientist') {
      // Lab cap + goggles strap (forehead)
      const cap = simpleBox(9*S, 2*S, 9*S, profData.hatColor);
      cap.position.set(0, 36.5*S, 0);
      group.add(cap);
      // Goggles (two black lenses)
      const gogL = simpleBox(2*S, 2*S, 0.5*S, 0x111111);
      gogL.position.set(-2*S, 2*S, 4.6*S);
      group.add(gogL);
      const gogR = simpleBox(2*S, 2*S, 0.5*S, 0x111111);
      gogR.position.set(2*S, 2*S, 4.6*S);
      group.add(gogR);
    } else if (profession === 'architect') {
      // Drafting beret (gold)
      const beret = simpleBox(10*S, 2*S, 10*S, profData.hatColor);
      beret.position.set(0, 36.5*S, 0);
      group.add(beret);
    }

    // Profession accessory on body (small identifier box)
    if (profData.accessory === 'cross_red' || profData.accessory === 'cross_green') {
      const c = profData.accessory === 'cross_red' ? 0xCC0000 : 0x00AA00;
      const crossV = simpleBox(1.5*S, 4*S, 0.5*S, c);
      crossV.position.set(0, 20*S, 2.2*S);
      group.add(crossV);
      const crossH = simpleBox(4*S, 1.5*S, 0.5*S, c);
      crossH.position.set(0, 20*S, 2.2*S);
      group.add(crossH);
    } else if (profData.accessory === 'tube_paper') {
      // Architect carries a rolled blueprint
      const tube = simpleBox(1.5*S, 8*S, 1.5*S, 0xEEEEEE);
      tube.position.set(7*S, 20*S, 0);
      group.add(tube);
    } else if (profData.accessory === 'goggles') {
      // Lab coat pocket
      const pocket = simpleBox(3*S, 3*S, 0.5*S, 0xCCCCCC);
      pocket.position.set(-2*S, 16*S, 2.2*S);
      group.add(pocket);
    } else if (profData.accessory === 'hardhat') {
      // Tool belt (brown band)
      const belt = simpleBox(9*S, 1.5*S, 4.5*S, 0x8B4513);
      belt.position.set(0, 14*S, 0);
      group.add(belt);
    }
  } else if (variant === 'rich') {
    // Fancy hat with gold trim
    const hatBrim = simpleBox(12*S, 1*S, 12*S, 0x4A1A4A);
    hatBrim.position.set(0, 36*S, 0);
    group.add(hatBrim);
    const hatTop = simpleBox(8*S, 5*S, 8*S, 0x4A1A4A);
    hatTop.position.set(0, 39*S, 0);
    group.add(hatTop);
    // Gold trim
    const trim = simpleBox(12.5*S, 1*S, 12.5*S, 0xFFAA22);
    trim.position.set(0, 35.5*S, 0);
    group.add(trim);
    // Emerald
    const gem = simpleBox(2*S, 2*S, 0.5*S, 0x00CC44);
    gem.position.set(0, 40*S, 4.2*S);
    group.add(gem);
  } else if (variant === 'poor') {
    // Tattered simple hat
    const hat = simpleBox(9*S, 1*S, 9*S, 0x3A2A1A);
    hat.position.set(0, 36*S, 0);
    group.add(hat);
  } else {
    // Normal villager hat
    const hatBrim = simpleBox(10*S, 1*S, 10*S, 0x5A3A1A);
    hatBrim.position.set(0, 36*S, 0);
    group.add(hatBrim);
    const hatTop = simpleBox(6*S, 3*S, 6*S, 0x5A3A1A);
    hatTop.position.set(0, 38.5*S, 0);
    group.add(hatTop);
  }

  return { headMesh: headGroup, legFL: null, legFR: null, legBL: null, legBR: null, armL, armR };
}

// ==================== WOLF ====================
function buildWolf(group, variant) {
  const S = PX * 1.8;

  const furGray   = 0xAAAAAA;
  const furDark   = 0x888888;
  const furWhite  = 0xEEEEEE;
  const noseBlack = 0x222222;
  const eyeColor  = 0x444444;

  // Body (8w x 6h x 12d)
  const body = simpleBox(8 * S, 6 * S, 12 * S, furGray);
  body.position.set(0, 8 * S, 0);
  group.add(body);

  // White belly
  const belly = simpleBox(6 * S, 3 * S, 10 * S, furWhite);
  belly.position.set(0, 5.5 * S, 0);
  group.add(belly);

  // Head (6x6x6)
  const headGroup = new THREE.Group();
  const head = simpleBox(6 * S, 6 * S, 6 * S, furGray);
  headGroup.add(head);

  // Snout (elongated, darker)
  const snout = simpleBox(3 * S, 3 * S, 4 * S, furDark);
  snout.position.set(0, -0.5 * S, 5 * S);
  headGroup.add(snout);

  // Nose (black tip)
  const nose = simpleBox(1.5 * S, 1.5 * S, 0.5 * S, noseBlack);
  nose.position.set(0, 0, 7.2 * S);
  headGroup.add(nose);

  // Eyes
  const eyeL = simpleBox(1.5 * S, 1.5 * S, 0.5 * S, 0xFFFFFF);
  eyeL.position.set(-1.5 * S, 1.5 * S, 3.2 * S);
  headGroup.add(eyeL);
  const eyeR = simpleBox(1.5 * S, 1.5 * S, 0.5 * S, 0xFFFFFF);
  eyeR.position.set(1.5 * S, 1.5 * S, 3.2 * S);
  headGroup.add(eyeR);
  const pupilL = simpleBox(0.8 * S, 0.8 * S, 0.5 * S, eyeColor);
  pupilL.position.set(-1.2 * S, 1.5 * S, 3.5 * S);
  headGroup.add(pupilL);
  const pupilR = simpleBox(0.8 * S, 0.8 * S, 0.5 * S, eyeColor);
  pupilR.position.set(1.8 * S, 1.5 * S, 3.5 * S);
  headGroup.add(pupilR);

  // Ears (pointed, triangular look with boxes)
  const earL = simpleBox(2 * S, 3 * S, 1 * S, furDark);
  earL.position.set(-2 * S, 5 * S, -1 * S);
  earL.rotation.z = -0.2;
  headGroup.add(earL);
  const earTipL = simpleBox(1 * S, 1.5 * S, 0.8 * S, furGray);
  earTipL.position.set(-2 * S, 7 * S, -1 * S);
  earTipL.rotation.z = -0.2;
  headGroup.add(earTipL);

  const earR = simpleBox(2 * S, 3 * S, 1 * S, furDark);
  earR.position.set(2 * S, 5 * S, -1 * S);
  earR.rotation.z = 0.2;
  headGroup.add(earR);
  const earTipR = simpleBox(1 * S, 1.5 * S, 0.8 * S, furGray);
  earTipR.position.set(2 * S, 7 * S, -1 * S);
  earTipR.rotation.z = 0.2;
  headGroup.add(earTipR);

  headGroup.position.set(0, 10 * S, 8 * S);
  group.add(headGroup);

  // Legs (3x6x3)
  const legH = 5 * S;
  const legW = 3 * S;
  const legColor = furDark;

  const legFL = new THREE.Group();
  const legFLmesh = simpleBox(legW, legH, legW, legColor);
  legFLmesh.position.set(0, -legH / 2, 0);
  legFL.add(legFLmesh);
  legFL.position.set(-2.5 * S, 5 * S + legH / 2, 4 * S);
  group.add(legFL);

  const legFR = new THREE.Group();
  const legFRmesh = simpleBox(legW, legH, legW, legColor);
  legFRmesh.position.set(0, -legH / 2, 0);
  legFR.add(legFRmesh);
  legFR.position.set(2.5 * S, 5 * S + legH / 2, 4 * S);
  group.add(legFR);

  const legBL = new THREE.Group();
  const legBLmesh = simpleBox(legW, legH, legW, legColor);
  legBLmesh.position.set(0, -legH / 2, 0);
  legBL.add(legBLmesh);
  legBL.position.set(-2.5 * S, 5 * S + legH / 2, -4 * S);
  group.add(legBL);

  const legBR = new THREE.Group();
  const legBRmesh = simpleBox(legW, legH, legW, legColor);
  legBRmesh.position.set(0, -legH / 2, 0);
  legBR.add(legBRmesh);
  legBR.position.set(2.5 * S, 5 * S + legH / 2, -4 * S);
  group.add(legBR);

  // Tail (angled up)
  const tail = simpleBox(1.5 * S, 5 * S, 1.5 * S, furGray);
  tail.position.set(0, 10 * S, -7 * S);
  tail.rotation.x = -0.5;
  group.add(tail);

  // Collar for tamed wolves (red)
  // This will be a hidden element; made visible when wolf is tamed
  const collar = simpleBox(7 * S, 1.5 * S, 1.5 * S, 0xDD2222);
  collar.position.set(0, 11 * S, 5 * S);
  collar.visible = false;
  collar.name = 'wolfCollar';
  group.add(collar);

  return { headMesh: headGroup, legFL, legFR, legBL, legBR };
}

// ==================== HUMANOID (Steve/Alex/Herobrine) ====================
// Standard Minecraft player model: 8x8x8 head, 8x12x4 torso, 4x12x4 arms, 4x12x4 legs
function buildHumanoid(group, skinColors) {
  const S = PX * 1.8;
  const { shirt, pants, skin, hair, eyes, eyeGlow } = skinColors;

  // Torso (8w x 12h x 4d)
  const torso = simpleBox(8*S, 12*S, 4*S, shirt);
  torso.position.set(0, 18*S, 0);
  group.add(torso);

  // Head (8x8x8)
  const headGroup = new THREE.Group();
  const head = simpleBox(8*S, 8*S, 8*S, skin);
  headGroup.add(head);

  // Hair (top + back of head)
  const hairTop = simpleBox(8.2*S, 2*S, 8.2*S, hair);
  hairTop.position.set(0, 5*S, 0);
  headGroup.add(hairTop);
  const hairBack = simpleBox(8.2*S, 8*S, 1*S, hair);
  hairBack.position.set(0, 0, -4.5*S);
  headGroup.add(hairBack);
  // Hair sides
  const hairL = simpleBox(1*S, 6*S, 8.2*S, hair);
  hairL.position.set(-4.5*S, 1*S, 0);
  headGroup.add(hairL);
  const hairR = simpleBox(1*S, 6*S, 8.2*S, hair);
  hairR.position.set(4.5*S, 1*S, 0);
  headGroup.add(hairR);

  // Eyes
  const eyeL = simpleBox(2*S, 2*S, 0.5*S, 0xFFFFFF);
  eyeL.position.set(-2*S, 1*S, 4.2*S);
  headGroup.add(eyeL);
  const eyeR = simpleBox(2*S, 2*S, 0.5*S, 0xFFFFFF);
  eyeR.position.set(2*S, 1*S, 4.2*S);
  headGroup.add(eyeR);

  if (eyeGlow) {
    // Herobrine: solid white glowing eyes (no pupils)
    const glowL = simpleBox(2.2*S, 2.2*S, 0.3*S, 0xFFFFFF);
    glowL.position.set(-2*S, 1*S, 4.4*S);
    headGroup.add(glowL);
    const glowR = simpleBox(2.2*S, 2.2*S, 0.3*S, 0xFFFFFF);
    glowR.position.set(2*S, 1*S, 4.4*S);
    headGroup.add(glowR);
    // Emissive glow effect
    const glowMat = new THREE.MeshBasicMaterial({ color: 0xFFFFFF });
    const glow2L = new THREE.Mesh(new THREE.BoxGeometry(2.5*S, 2.5*S, 0.2*S), glowMat);
    glow2L.position.set(-2*S, 1*S, 4.6*S);
    headGroup.add(glow2L);
    const glow2R = new THREE.Mesh(new THREE.BoxGeometry(2.5*S, 2.5*S, 0.2*S), glowMat);
    glow2R.position.set(2*S, 1*S, 4.6*S);
    headGroup.add(glow2R);
  } else {
    // Normal pupils
    const pupilColor = eyes || 0x222222;
    const pupilL = simpleBox(1*S, 1.5*S, 0.5*S, pupilColor);
    pupilL.position.set(-1.5*S, 1*S, 4.5*S);
    headGroup.add(pupilL);
    const pupilR = simpleBox(1*S, 1.5*S, 0.5*S, pupilColor);
    pupilR.position.set(2.5*S, 1*S, 4.5*S);
    headGroup.add(pupilR);
  }

  // Mouth
  const mouth = simpleBox(4*S, 1*S, 0.5*S, 0x8B6B5A);
  mouth.position.set(0, -2*S, 4.2*S);
  headGroup.add(mouth);

  // Nose (subtle)
  const nose = simpleBox(1.5*S, 1.5*S, 1*S, skin);
  nose.position.set(0, 0, 4.5*S);
  headGroup.add(nose);

  headGroup.position.set(0, 28*S, 0);
  group.add(headGroup);

  // Arms (4w x 12h x 4d)
  const armL = new THREE.Group();
  const armLupper = simpleBox(4*S, 12*S, 4*S, shirt);
  armLupper.position.y = -6*S;
  armL.add(armLupper);
  // Hand (skin colored)
  const handL = simpleBox(4*S, 2*S, 4*S, skin);
  handL.position.set(0, -13*S, 0);
  armL.add(handL);
  armL.position.set(-6*S, 24*S, 0);
  group.add(armL);

  const armR = new THREE.Group();
  const armRupper = simpleBox(4*S, 12*S, 4*S, shirt);
  armRupper.position.y = -6*S;
  armR.add(armRupper);
  const handR = simpleBox(4*S, 2*S, 4*S, skin);
  handR.position.set(0, -13*S, 0);
  armR.add(handR);
  armR.position.set(6*S, 24*S, 0);
  group.add(armR);

  // Legs (4w x 12h x 4d)
  const legFL = new THREE.Group();
  const legLmesh = simpleBox(4*S, 12*S, 4*S, pants);
  legLmesh.position.y = -6*S;
  legFL.add(legLmesh);
  // Shoe
  const shoeL = simpleBox(4.2*S, 2*S, 4.5*S, 0x444444);
  shoeL.position.set(0, -12.5*S, 0.2*S);
  legFL.add(shoeL);
  legFL.position.set(-2*S, 12*S, 0);
  group.add(legFL);

  const legFR = new THREE.Group();
  const legRmesh = simpleBox(4*S, 12*S, 4*S, pants);
  legRmesh.position.y = -6*S;
  legFR.add(legRmesh);
  const shoeR = simpleBox(4.2*S, 2*S, 4.5*S, 0x444444);
  shoeR.position.set(0, -12.5*S, 0.2*S);
  legFR.add(shoeR);
  legFR.position.set(2*S, 12*S, 0);
  group.add(legFR);

  return { headMesh: headGroup, legFL, legFR, legBL: null, legBR: null, armL, armR };
}

function buildSteve(group) {
  return buildHumanoid(group, {
    shirt: 0x00AAAA,  // Teal/cyan shirt
    pants: 0x2222AA,  // Blue jeans
    skin: 0xBB8866,   // Skin tone
    hair: 0x443322,   // Dark brown hair
    eyes: 0x443399,   // Dark blue-purple eyes
    eyeGlow: false,
  });
}

function buildAlex(group) {
  return buildHumanoid(group, {
    shirt: 0x6AAA33,  // Green shirt
    pants: 0x775533,  // Brown pants
    skin: 0xDDBB99,   // Lighter skin
    hair: 0xCC6622,   // Orange/ginger hair
    eyes: 0x338833,   // Green eyes
    eyeGlow: false,
  });
}

function buildHerobrine(group) {
  const refs = buildHumanoid(group, {
    shirt: 0x00AAAA,  // Same teal shirt as Steve
    pants: 0x2222AA,  // Same blue jeans
    skin: 0xBB8866,   // Same skin
    hair: 0x443322,   // Same dark brown hair
    eyes: 0xFFFFFF,   // NOT used - eyeGlow overrides
    eyeGlow: true,    // WHITE GLOWING EYES
  });

  // Add subtle point light for eerie glow from eyes
  const glow = new THREE.PointLight(0xFFFFFF, 0.5, 5);
  glow.position.set(0, 1 * PX * 1.8, 5 * PX * 1.8);
  refs.headMesh.add(glow);

  return refs;
}

// ==================== HORSE ====================
function buildHorse(group, variant) {
  const S = PX * 1.8;

  // Pick color variant
  let bodyColor;
  const r = Math.random();
  if (variant === 'brown' || (!variant && r < 0.33)) {
    bodyColor = 0x8B4513;
    if (!variant) variant = 'brown';
  } else if (variant === 'black' || (!variant && r < 0.66)) {
    bodyColor = 0x2A2A2A;
    if (!variant) variant = 'black';
  } else {
    bodyColor = 0xE8E0D0;
    if (!variant) variant = 'white';
  }
  const darkColor = variant === 'white' ? 0xC8C0B0 : variant === 'black' ? 0x1A1A1A : 0x6B3410;

  // Body (20w x 14h x 10d)
  const body = simpleBox(20*S, 14*S, 10*S, bodyColor);
  body.position.set(0, 16*S, 0);
  group.add(body);

  // Neck (6w x 10h x 6d, angled forward)
  const neck = simpleBox(6*S, 10*S, 6*S, bodyColor);
  neck.position.set(0, 24*S, 8*S);
  neck.rotation.x = 0.4;
  group.add(neck);

  // Mane on neck
  const mane = simpleBox(1*S, 8*S, 5*S, darkColor);
  mane.position.set(0, 26*S, 7*S);
  mane.rotation.x = 0.4;
  group.add(mane);

  // Head (8w x 8h x 6d)
  const headGroup = new THREE.Group();
  const head = simpleBox(8*S, 8*S, 6*S, bodyColor);
  headGroup.add(head);

  // Muzzle (lighter/darker)
  const muzzle = simpleBox(6*S, 4*S, 2*S, darkColor);
  muzzle.position.set(0, -1*S, 4*S);
  headGroup.add(muzzle);

  // Nostrils
  const nostrilL = simpleBox(1*S, 1*S, 0.5*S, 0x222222);
  nostrilL.position.set(-1.5*S, -2*S, 5.2*S);
  headGroup.add(nostrilL);
  const nostrilR = simpleBox(1*S, 1*S, 0.5*S, 0x222222);
  nostrilR.position.set(1.5*S, -2*S, 5.2*S);
  headGroup.add(nostrilR);

  // Eyes
  const eyeL = simpleBox(2*S, 2*S, 0.5*S, 0xFFFFFF);
  eyeL.position.set(-2.5*S, 2*S, 3.2*S);
  headGroup.add(eyeL);
  const eyeR = simpleBox(2*S, 2*S, 0.5*S, 0xFFFFFF);
  eyeR.position.set(2.5*S, 2*S, 3.2*S);
  headGroup.add(eyeR);
  const pupilL = simpleBox(1*S, 1.5*S, 0.5*S, 0x222222);
  pupilL.position.set(-2*S, 2*S, 3.5*S);
  headGroup.add(pupilL);
  const pupilR = simpleBox(1*S, 1.5*S, 0.5*S, 0x222222);
  pupilR.position.set(3*S, 2*S, 3.5*S);
  headGroup.add(pupilR);

  // Ears (pointed)
  const earL = simpleBox(2*S, 3*S, 1*S, bodyColor);
  earL.position.set(-2.5*S, 6*S, -1*S);
  earL.rotation.z = -0.3;
  headGroup.add(earL);
  const earR = simpleBox(2*S, 3*S, 1*S, bodyColor);
  earR.position.set(2.5*S, 6*S, -1*S);
  earR.rotation.z = 0.3;
  headGroup.add(earR);

  headGroup.position.set(0, 30*S, 14*S);
  group.add(headGroup);

  // Legs (3w x 12h x 3d - long legs)
  const legH = 12*S;
  const legW = 3*S;
  const legColor = bodyColor;

  const legFL = new THREE.Group();
  const legFLmesh = simpleBox(legW, legH, legW, legColor);
  legFLmesh.position.set(0, -legH/2, 0);
  legFL.add(legFLmesh);
  legFL.position.set(-4*S, 9*S + legH/2, 7*S);
  group.add(legFL);

  const legFR = new THREE.Group();
  const legFRmesh = simpleBox(legW, legH, legW, legColor);
  legFRmesh.position.set(0, -legH/2, 0);
  legFR.add(legFRmesh);
  legFR.position.set(4*S, 9*S + legH/2, 7*S);
  group.add(legFR);

  const legBL = new THREE.Group();
  const legBLmesh = simpleBox(legW, legH, legW, legColor);
  legBLmesh.position.set(0, -legH/2, 0);
  legBL.add(legBLmesh);
  legBL.position.set(-4*S, 9*S + legH/2, -7*S);
  group.add(legBL);

  const legBR = new THREE.Group();
  const legBRmesh = simpleBox(legW, legH, legW, legColor);
  legBRmesh.position.set(0, -legH/2, 0);
  legBR.add(legBRmesh);
  legBR.position.set(4*S, 9*S + legH/2, -7*S);
  group.add(legBR);

  // Tail
  const tail = simpleBox(1*S, 8*S, 1*S, darkColor);
  tail.position.set(0, 18*S, -11*S);
  tail.rotation.x = -0.4;
  group.add(tail);

  return { headMesh: headGroup, legFL, legFR, legBL, legBR };
}

// ==================== CAT ====================
function buildCat(group, variant) {
  const S = PX * 1.8;

  // Pick color variant
  let bodyColor, darkFace = null;
  const r = Math.random();
  if (variant === 'orange' || (!variant && r < 0.33)) {
    bodyColor = 0xE87020;
    if (!variant) variant = 'orange';
  } else if (variant === 'black' || (!variant && r < 0.66)) {
    bodyColor = 0x222222;
    if (!variant) variant = 'black';
  } else {
    bodyColor = 0xE8D4B8;
    darkFace = 0x6B4226;
    if (!variant) variant = 'siamese';
  }
  const bellyColor = variant === 'black' ? 0x333333 : 0xF4F0E8;

  // Body (6w x 5h x 12d)
  const body = simpleBox(6*S, 5*S, 12*S, bodyColor);
  body.position.set(0, 6*S, 0);
  group.add(body);

  // Belly
  const belly = simpleBox(4*S, 2*S, 10*S, bellyColor);
  belly.position.set(0, 4*S, 0);
  group.add(belly);

  // Head (5x5x5)
  const headGroup = new THREE.Group();
  const headColor = darkFace || bodyColor;
  const head = simpleBox(5*S, 5*S, 5*S, headColor);
  headGroup.add(head);

  // Siamese: lighter top of head
  if (darkFace) {
    const lightTop = simpleBox(5.1*S, 2*S, 5.1*S, bodyColor);
    lightTop.position.set(0, 2*S, 0);
    headGroup.add(lightTop);
  }

  // Nose (pink)
  const nose = simpleBox(1*S, 0.5*S, 0.5*S, 0xFFAAAA);
  nose.position.set(0, -0.5*S, 2.8*S);
  headGroup.add(nose);

  // Eyes (large, cat-like)
  const eyeL = simpleBox(1.5*S, 1.5*S, 0.5*S, 0xFFFFFF);
  eyeL.position.set(-1.2*S, 1*S, 2.7*S);
  headGroup.add(eyeL);
  const eyeR = simpleBox(1.5*S, 1.5*S, 0.5*S, 0xFFFFFF);
  eyeR.position.set(1.2*S, 1*S, 2.7*S);
  headGroup.add(eyeR);
  // Vertical slit pupils
  const pupilL = simpleBox(0.5*S, 1.5*S, 0.5*S, 0x222222);
  pupilL.position.set(-1*S, 1*S, 3*S);
  headGroup.add(pupilL);
  const pupilR = simpleBox(0.5*S, 1.5*S, 0.5*S, 0x222222);
  pupilR.position.set(1.4*S, 1*S, 3*S);
  headGroup.add(pupilR);

  // Ears (pointed triangles approximated with small boxes)
  const earL = simpleBox(2*S, 3*S, 1*S, bodyColor);
  earL.position.set(-1.5*S, 4.5*S, -0.5*S);
  earL.rotation.z = -0.2;
  headGroup.add(earL);
  const earInL = simpleBox(1*S, 2*S, 0.5*S, 0xFFAAAA);
  earInL.position.set(-1.5*S, 4.5*S, 0);
  earInL.rotation.z = -0.2;
  headGroup.add(earInL);
  const earR = simpleBox(2*S, 3*S, 1*S, bodyColor);
  earR.position.set(1.5*S, 4.5*S, -0.5*S);
  earR.rotation.z = 0.2;
  headGroup.add(earR);
  const earInR = simpleBox(1*S, 2*S, 0.5*S, 0xFFAAAA);
  earInR.position.set(1.5*S, 4.5*S, 0);
  earInR.rotation.z = 0.2;
  headGroup.add(earInR);

  // Whiskers (thin boxes)
  const whiskerMat = new THREE.MeshLambertMaterial({ color: 0xCCCCCC });
  const wL1 = new THREE.Mesh(new THREE.BoxGeometry(4*S, 0.2*S, 0.2*S), whiskerMat);
  wL1.position.set(-3*S, 0, 2.5*S);
  wL1.rotation.z = 0.1;
  headGroup.add(wL1);
  const wL2 = new THREE.Mesh(new THREE.BoxGeometry(4*S, 0.2*S, 0.2*S), whiskerMat);
  wL2.position.set(-3*S, -0.5*S, 2.5*S);
  wL2.rotation.z = -0.1;
  headGroup.add(wL2);
  const wR1 = new THREE.Mesh(new THREE.BoxGeometry(4*S, 0.2*S, 0.2*S), whiskerMat);
  wR1.position.set(3*S, 0, 2.5*S);
  wR1.rotation.z = -0.1;
  headGroup.add(wR1);
  const wR2 = new THREE.Mesh(new THREE.BoxGeometry(4*S, 0.2*S, 0.2*S), whiskerMat);
  wR2.position.set(3*S, -0.5*S, 2.5*S);
  wR2.rotation.z = 0.1;
  headGroup.add(wR2);

  headGroup.position.set(0, 9*S, 8*S);
  group.add(headGroup);

  // Legs (2w x 4h x 2d - short)
  const legH = 4*S;
  const legW = 2*S;
  const legColor = bodyColor;

  const legFL = new THREE.Group();
  const legFLmesh = simpleBox(legW, legH, legW, legColor);
  legFLmesh.position.set(0, -legH/2, 0);
  legFL.add(legFLmesh);
  legFL.position.set(-2*S, 3.5*S + legH/2, 4*S);
  group.add(legFL);

  const legFR = new THREE.Group();
  const legFRmesh = simpleBox(legW, legH, legW, legColor);
  legFRmesh.position.set(0, -legH/2, 0);
  legFR.add(legFRmesh);
  legFR.position.set(2*S, 3.5*S + legH/2, 4*S);
  group.add(legFR);

  const legBL = new THREE.Group();
  const legBLmesh = simpleBox(legW, legH, legW, legColor);
  legBLmesh.position.set(0, -legH/2, 0);
  legBL.add(legBLmesh);
  legBL.position.set(-2*S, 3.5*S + legH/2, -4*S);
  group.add(legBL);

  const legBR = new THREE.Group();
  const legBRmesh = simpleBox(legW, legH, legW, legColor);
  legBRmesh.position.set(0, -legH/2, 0);
  legBR.add(legBRmesh);
  legBR.position.set(2*S, 3.5*S + legH/2, -4*S);
  group.add(legBR);

  // Tail (long, curved)
  const tail = simpleBox(1*S, 1*S, 6*S, bodyColor);
  tail.position.set(0, 8*S, -9*S);
  tail.rotation.x = -0.6;
  group.add(tail);
  const tailTip = simpleBox(1*S, 1*S, 3*S, bodyColor);
  tailTip.position.set(0, 11*S, -12*S);
  tailTip.rotation.x = -1.0;
  group.add(tailTip);

  return { headMesh: headGroup, legFL, legFR, legBL, legBR };
}

// ==================== BEE ====================
function buildBee(group) {
  const S = PX * 1.8;

  const yellow = 0xF5C518;
  const black = 0x222222;

  // Body: alternating yellow and black stripes (4w x 4h x 6d total)
  // 3 stripes: yellow-black-yellow
  const stripe1 = simpleBox(4*S, 4*S, 2*S, yellow);
  stripe1.position.set(0, 4*S, 2*S);
  group.add(stripe1);
  const stripe2 = simpleBox(4*S, 4*S, 2*S, black);
  stripe2.position.set(0, 4*S, 0);
  group.add(stripe2);
  const stripe3 = simpleBox(4*S, 4*S, 2*S, yellow);
  stripe3.position.set(0, 4*S, -2*S);
  group.add(stripe3);

  // Face (front, dark)
  const face = simpleBox(4.1*S, 4.1*S, 0.5*S, black);
  face.position.set(0, 4*S, 3.2*S);
  group.add(face);

  // Eyes (white dots on face)
  const eyeL = simpleBox(1*S, 1*S, 0.3*S, 0xFFFFFF);
  eyeL.position.set(-1*S, 5*S, 3.5*S);
  group.add(eyeL);
  const eyeR = simpleBox(1*S, 1*S, 0.3*S, 0xFFFFFF);
  eyeR.position.set(1*S, 5*S, 3.5*S);
  group.add(eyeR);

  // Stinger (back)
  const stinger = simpleBox(0.5*S, 0.5*S, 1*S, 0x444444);
  stinger.position.set(0, 3.5*S, -3.5*S);
  group.add(stinger);

  // Wings (translucent, 4w x 1h x 3d each)
  const wingMat = new THREE.MeshLambertMaterial({ color: 0xFFFFFF, transparent: true, opacity: 0.4 });
  const wingL = new THREE.Mesh(new THREE.BoxGeometry(4*S, 0.5*S, 3*S), wingMat);
  wingL.position.set(-3*S, 6.5*S, 0);
  wingL.rotation.z = 0.2;
  group.add(wingL);
  const wingR = new THREE.Mesh(new THREE.BoxGeometry(4*S, 0.5*S, 3*S), wingMat);
  wingR.position.set(3*S, 6.5*S, 0);
  wingR.rotation.z = -0.2;
  group.add(wingR);

  // Antennae
  const antennaL = simpleBox(0.3*S, 2*S, 0.3*S, black);
  antennaL.position.set(-1*S, 7*S, 2.5*S);
  antennaL.rotation.x = 0.4;
  group.add(antennaL);
  const antennaR = simpleBox(0.3*S, 2*S, 0.3*S, black);
  antennaR.position.set(1*S, 7*S, 2.5*S);
  antennaR.rotation.x = 0.4;
  group.add(antennaR);

  return { headMesh: null, legFL: null, legFR: null, legBL: null, legBR: null };
}

// ==================== IRON GOLEM MESH ====================
function buildIronGolem(group) {
  const S = PX * 2.2; // larger scale

  // Body (wide and tall)
  const body = simpleBox(14*S, 20*S, 8*S, 0xC8C8C8);
  body.position.y = 22*S;
  group.add(body);

  // Head (small relative to body)
  const head = simpleBox(8*S, 6*S, 6*S, 0xC8C8C8);
  head.position.y = 36*S;
  group.add(head);

  // Nose
  const nose = simpleBox(2*S, 4*S, 2*S, 0xB0B0B0);
  nose.position.set(0, 35*S, 4*S);
  group.add(nose);

  // Eyes (dark)
  const eyeL = simpleBox(2*S, 1.5*S, 1*S, 0x333333);
  eyeL.position.set(-2*S, 37*S, 3.5*S);
  group.add(eyeL);
  const eyeR = simpleBox(2*S, 1.5*S, 1*S, 0x333333);
  eyeR.position.set(2*S, 37*S, 3.5*S);
  group.add(eyeR);

  // Arms (long, thick)
  const armL = simpleBox(4*S, 22*S, 5*S, 0xB8B8B8);
  armL.position.set(-9*S, 18*S, 0);
  group.add(armL);
  const armR = simpleBox(4*S, 22*S, 5*S, 0xB8B8B8);
  armR.position.set(9*S, 18*S, 0);
  group.add(armR);

  // Vine/rose decoration on body
  const vine = simpleBox(3*S, 3*S, 1*S, 0x4CAF50);
  vine.position.set(3*S, 26*S, 4.5*S);
  group.add(vine);
  const rose = simpleBox(2*S, 2*S, 1*S, 0xDD2222);
  rose.position.set(3*S, 29*S, 4.5*S);
  group.add(rose);

  // Legs (short, thick)
  const legL = simpleBox(5*S, 10*S, 5*S, 0xB8B8B8);
  legL.position.set(-4*S, 5*S, 0);
  group.add(legL);
  const legR = simpleBox(5*S, 10*S, 5*S, 0xB8B8B8);
  legR.position.set(4*S, 5*S, 0);
  group.add(legR);

  return { headMesh: head, legFL: legL, legFR: legR, legBL: null, legBR: null, armL, armR };
}

// ==================== SNOW GOLEM MESH ====================
function buildSnowGolem(group) {
  const S = PX * 1.8;

  // Bottom body (large snowball)
  const bodyBottom = simpleBox(10*S, 10*S, 10*S, 0xF0F0FF);
  bodyBottom.position.y = 5*S;
  group.add(bodyBottom);

  // Top body (medium snowball)
  const bodyTop = simpleBox(8*S, 8*S, 8*S, 0xF0F0FF);
  bodyTop.position.y = 14*S;
  group.add(bodyTop);

  // Head (pumpkin)
  const head = simpleBox(8*S, 8*S, 8*S, 0xD4760A);
  head.position.y = 22*S;
  group.add(head);

  // Pumpkin face
  const eyeL = simpleBox(2*S, 2*S, 1*S, 0x222222);
  eyeL.position.set(-2*S, 23*S, 4.5*S);
  group.add(eyeL);
  const eyeR = simpleBox(2*S, 2*S, 1*S, 0x222222);
  eyeR.position.set(2*S, 23*S, 4.5*S);
  group.add(eyeR);
  const mouth = simpleBox(4*S, 2*S, 1*S, 0x222222);
  mouth.position.set(0, 20*S, 4.5*S);
  group.add(mouth);

  // Stick arms
  const armL = simpleBox(1*S, 8*S, 1*S, 0x6B4226);
  armL.position.set(-6*S, 15*S, 0);
  armL.rotation.z = 0.6;
  group.add(armL);
  const armR = simpleBox(1*S, 8*S, 1*S, 0x6B4226);
  armR.position.set(6*S, 15*S, 0);
  armR.rotation.z = -0.6;
  group.add(armR);

  // Buttons
  for (let i = 0; i < 3; i++) {
    const button = simpleBox(1.5*S, 1.5*S, 1*S, 0x222222);
    button.position.set(0, (6 + i*4)*S, 5.5*S);
    group.add(button);
  }

  return { headMesh: head, legFL: null, legFR: null, legBL: null, legBR: null, armL, armR };
}

// ==================== CAR ====================
function buildCar(group, variant) {
  // Approx 2.5 blocks long, 1.2 wide, 1.0 tall
  const colors = {
    red:   { body: 0xCC2A2A, accent: 0x991C1C },
    blue:  { body: 0x2A5ACC, accent: 0x1C3D8A },
    yellow:{ body: 0xE6C72A, accent: 0xB39822 },
    black: { body: 0x222222, accent: 0x111111 },
  };
  const c = colors[variant] || colors.red;

  // Chassis (lower half)
  const chassis = simpleBox(1.2, 0.4, 2.4, c.accent);
  chassis.position.set(0, 0.3, 0);
  group.add(chassis);

  // Cabin (upper half — smaller)
  const cabin = simpleBox(1.05, 0.5, 1.4, c.body);
  cabin.position.set(0, 0.75, -0.1);
  group.add(cabin);

  // Roof
  const roof = simpleBox(1.0, 0.1, 1.2, c.accent);
  roof.position.set(0, 1.05, -0.1);
  group.add(roof);

  // Windshield (front glass)
  const windshield = simpleBox(0.9, 0.4, 0.05, 0x88CCFF);
  windshield.material.transparent = true;
  windshield.material.opacity = 0.5;
  windshield.position.set(0, 0.75, 0.6);
  group.add(windshield);

  // Rear window
  const rear = simpleBox(0.9, 0.4, 0.05, 0x88CCFF);
  rear.material.transparent = true;
  rear.material.opacity = 0.5;
  rear.position.set(0, 0.75, -0.8);
  group.add(rear);

  // 4 wheels
  const wheelGeo = new THREE.CylinderGeometry(0.22, 0.22, 0.18, 12);
  const wheelMat = new THREE.MeshLambertMaterial({ color: 0x101010 });
  for (const [x, z] of [[-0.55, 0.75], [0.55, 0.75], [-0.55, -0.75], [0.55, -0.75]]) {
    const w = new THREE.Mesh(wheelGeo, wheelMat);
    w.rotation.z = Math.PI / 2;
    w.position.set(x, 0.22, z);
    group.add(w);
  }

  // Headlights
  const hlGeo = new THREE.BoxGeometry(0.2, 0.12, 0.05);
  const hlMat = new THREE.MeshLambertMaterial({ color: 0xFFFFCC, emissive: 0xFFEE88 });
  for (const x of [-0.35, 0.35]) {
    const hl = new THREE.Mesh(hlGeo, hlMat);
    hl.position.set(x, 0.4, 1.2);
    group.add(hl);
  }

  return { headMesh: null, legFL: null, legFR: null, legBL: null, legBR: null, armL: null, armR: null };
}

// ==================== MOTO ====================
function buildMoto(group, variant) {
  const c = variant === 'sport' ? 0xCC2A2A : 0x222222;

  // Frame (long horizontal bar)
  const frame = simpleBox(0.3, 0.2, 1.6, c);
  frame.position.set(0, 0.6, 0);
  group.add(frame);

  // Seat
  const seat = simpleBox(0.35, 0.1, 0.5, 0x111111);
  seat.position.set(0, 0.78, -0.1);
  group.add(seat);

  // Fuel tank
  const tank = simpleBox(0.35, 0.25, 0.5, c);
  tank.position.set(0, 0.78, 0.35);
  group.add(tank);

  // Handlebars (T shape)
  const handle = simpleBox(0.6, 0.05, 0.05, 0x444444);
  handle.position.set(0, 1.05, 0.7);
  group.add(handle);
  const stem = simpleBox(0.05, 0.3, 0.05, 0x444444);
  stem.position.set(0, 0.9, 0.7);
  group.add(stem);

  // Wheels (front & back)
  const wheelGeo = new THREE.CylinderGeometry(0.35, 0.35, 0.18, 14);
  const wheelMat = new THREE.MeshLambertMaterial({ color: 0x101010 });
  for (const z of [0.7, -0.7]) {
    const w = new THREE.Mesh(wheelGeo, wheelMat);
    w.rotation.z = Math.PI / 2;
    w.position.set(0, 0.35, z);
    group.add(w);
  }

  // Headlight
  const hl = simpleBox(0.18, 0.18, 0.08, 0xFFFFCC);
  hl.material.emissive = new THREE.Color(0xFFEE88);
  hl.position.set(0, 0.7, 0.95);
  group.add(hl);

  return { headMesh: null, legFL: null, legFR: null, legBL: null, legBR: null, armL: null, armR: null };
}

// ==================== MOB REGISTRY ====================
const MOB_TYPES = {
  PIG:       { speed: 1.5, build: buildPig },
  SHEEP:     { speed: 1.2, build: buildSheep },
  COW:       { speed: 1.3, build: buildCow },
  CHICKEN:   { speed: 2.0, build: buildChicken },
  WOLF:      { speed: 3.0, build: buildWolf, isWolf: true },
  VILLAGER:  { speed: 1.0, build: buildVillager, isVillager: true },
  STEVE:     { speed: 1.4, build: buildSteve, isHumanoid: true },
  ALEX:      { speed: 1.4, build: buildAlex, isHumanoid: true },
  HEROBRINE: { speed: 0, build: buildHerobrine, isHerobrine: true },
  HORSE:     { speed: 4.0, build: buildHorse, isHorse: true },
  CAT:       { speed: 2.0, build: buildCat, isCat: true },
  BEE:       { speed: 2.5, build: buildBee, isBee: true },
  IRON_GOLEM: { speed: 1.5, build: buildIronGolem, isGolem: true },
  SNOW_GOLEM: { speed: 2.0, build: buildSnowGolem, isGolem: true },
  CAR:       { speed: 0, build: buildCar, isVehicle: true },
  MOTO:      { speed: 0, build: buildMoto, isVehicle: true },
};

// Education levels for villagers
const EDUCATION_LEVELS = [
  'Sin Estudios', 'Primaria', 'Secundaria', 'Universitario',
  'Licenciado', 'Magíster', 'Doctor', 'Postdoctorado',
];

const POOR_EDUCATION = ['Sin Estudios', 'Sin Estudios', 'Sin Estudios', 'Primaria'];
const RICH_EDUCATION = ['Universitario', 'Licenciado', 'Magíster', 'Doctor', 'Postdoctorado'];

// Professions for villagers (only assigned to rich villagers near Villa Prosperidad or Ciudad)
// Each profession has: label (shown in nameTag), hatColor, robeColor (overrides variant),
// and accessory (small box on body to identify the profession at a glance)
const PROFESSIONS = {
  doctor:     { label: 'Doctor',     hatColor: 0xFFFFFF, robeColor: 0xFFFFFF, accessory: 'cross_red'    },
  architect:  { label: 'Arquitecto', hatColor: 0xFFC107, robeColor: 0x5A4A3A, accessory: 'tube_paper'   },
  scientist:  { label: 'Científico', hatColor: 0xCCCCCC, robeColor: 0xEEEEEE, accessory: 'goggles'      },
  vet:        { label: 'Veterinario',hatColor: 0x66BB66, robeColor: 0x88AA66, accessory: 'cross_green'  },
  builder:    { label: 'Constructor',hatColor: 0xFFCC22, robeColor: 0xFFA500, accessory: 'hardhat'      },
};

export class EntityManager {
  constructor(scene, world, soundManager) {
    this.scene = scene;
    this.world = world;
    this.soundManager = soundManager;
    this.entities = [];
    this.maxEntities = MAX_ENTITIES;
    this.spawnTimer = 0;
    this.spawnInterval = SPAWN_INTERVAL;
    this.herobrineActive = false;
    this.herobrineTimer = HEROBRINE_FIRST_TIMER_BASE + Math.random() * HEROBRINE_FIRST_TIMER_RANGE;
    this.herobrineMessageShown = false;
    // Villa positions (set from main.js)
    this.villaMiseriaPos = null;
    this.villaProsperidadPos = null;

    // Shared blob shadow texture (generated once, reused by all entities)
    this._shadowTexture = this._createShadowTexture();
    this._shadowGeo = new THREE.PlaneGeometry(1, 1);
  }

  setVillaPositions(miseria, prosperidad) {
    this.villaMiseriaPos = miseria;
    this.villaProsperidadPos = prosperidad;
  }

  setCiudadPosition(ciudad) {
    this.ciudadPos = ciudad;
    this._vehiclesSpawned = false;
  }

  // Spawn cars and motos around the Ciudad once player gets close
  _maybeSpawnCityVehicles(playerPos) {
    if (this._vehiclesSpawned || !this.ciudadPos) return;
    const d = Math.hypot(playerPos.x - this.ciudadPos.x, playerPos.z - this.ciudadPos.z);
    if (d > 60) return; // wait until player is near

    const cx = this.ciudadPos.x;
    const cy = this.ciudadPos.y;
    const cz = this.ciudadPos.z;
    // Park vehicles along main streets (avoiding building footprints)
    const spawnSpots = [
      { dx: -14, dz:   0, type: 'CAR',  variant: 'red' },
      { dx:   8, dz:   0, type: 'CAR',  variant: 'blue' },
      { dx:   0, dz: -14, type: 'CAR',  variant: 'yellow' },
      { dx:   0, dz:   8, type: 'MOTO', variant: 'sport' },
      { dx: -14, dz: -14, type: 'MOTO', variant: 'black' },
      { dx:  14, dz:  14, type: 'CAR',  variant: 'black' },
      { dx:  14, dz: -14, type: 'MOTO', variant: 'sport' },
      { dx: -14, dz:  14, type: 'CAR',  variant: 'red' },
    ];

    for (const s of spawnSpots) {
      const wx = cx + s.dx;
      const wz = cz + s.dz;
      const wy = this.world.getTopBlock(wx, wz) + 1;
      const e = new Entity(s.type, wx + 0.5, wy, wz + 0.5);
      e.variant = s.variant;
      e.health = 100;
      e.maxHealth = 100;
      // Build mesh
      const cfg = MOB_TYPES[s.type];
      const group = new THREE.Group();
      const refs = cfg.build(group, s.variant);
      Object.assign(e, refs);
      group.position.copy(e.position);
      // Random street-aligned rotation
      group.rotation.y = (Math.abs(s.dz) > Math.abs(s.dx)) ? 0 : Math.PI / 2;
      e.mesh = group;
      this.scene.add(group);
      this.entities.push(e);
    }
    this._vehiclesSpawned = true;
  }

  update(dt, playerPos) {
    this._lastPlayerPos = playerPos;
    this._playerRef = playerPos;
    this._updateLightningBolts(dt);
    this._maybeSpawnCityVehicles(playerPos);
    this.spawnTimer -= dt;
    if (this.spawnTimer <= 0 && this.entities.length < this.maxEntities) {
      this._trySpawn(playerPos);
      this.spawnTimer = this.spawnInterval;
    }

    // Herobrine spawn timer
    this.herobrineTimer -= dt;
    if (this.herobrineTimer <= 0 && !this.herobrineActive) {
      this._spawnHerobrine(playerPos);
      this.herobrineTimer = HEROBRINE_RESPAWN_BASE + Math.random() * HEROBRINE_RESPAWN_RANGE;
    }

    for (let i = this.entities.length - 1; i >= 0; i--) {
      const e = this.entities[i];
      const dist = e.position.distanceTo(playerPos);

      // Herobrine special behavior
      if (e.type === 'HEROBRINE') {
        this._updateHerobrine(e, dt, playerPos, i);
        continue;
      }

      // Vehicles: persistent, no AI, no despawn, gravity only
      if (e.type === 'CAR' || e.type === 'MOTO') {
        if (!e._isRidden) {
          this._applyPhysics(e, dt);
        }
        if (e.mesh) e.mesh.position.set(e.position.x, e.position.y, e.position.z);
        this._updateBlobShadow(e);
        continue;
      }

      if (dist > DESPAWN_DISTANCE) {
        this._removeEntity(i);
        continue;
      }
      e.animTime += dt;
      this._updateAI(e, dt);
      this._applyPhysics(e, dt);
      if (e.mesh) {
        e.mesh.position.set(e.position.x, e.position.y, e.position.z);
        this._animate(e, dt);
      }
      // Update blob shadow
      this._updateBlobShadow(e);

      // Ambient mob sounds
      e.soundTimer -= dt;
      if (e.soundTimer <= 0 && this.soundManager) {
        this.soundManager.playMobSound(e.type, playerPos, e.position);
        const interval = SOUND_INTERVALS[e.type] || [5, 15];
        e.soundTimer = interval[0] + Math.random() * (interval[1] - interval[0]);
      }
    }
  }

  _animate(entity, dt) {
    const isMoving = entity.velocity.x !== 0 || entity.velocity.z !== 0;
    const speed = isMoving ? WALK_ANIM_SPEED : 0;
    const amp = isMoving ? WALK_ANIM_AMPLITUDE : 0;
    const t = entity.animTime;

    // Leg animation (pivot from top)
    if (entity.legFL) entity.legFL.rotation.x = Math.sin(t * speed) * amp;
    if (entity.legFR) entity.legFR.rotation.x = -Math.sin(t * speed) * amp;
    if (entity.legBL) entity.legBL.rotation.x = -Math.sin(t * speed) * amp;
    if (entity.legBR) entity.legBR.rotation.x = Math.sin(t * speed) * amp;

    // Arm swing for villagers
    if (entity.armL) entity.armL.rotation.x = Math.sin(t * speed) * amp * ARM_SWING_FACTOR;
    if (entity.armR) entity.armR.rotation.x = -Math.sin(t * speed) * amp * ARM_SWING_FACTOR;

    // Head bob
    if (entity.headMesh && isMoving) {
      entity.headMesh.rotation.x = Math.sin(t * speed * HEAD_BOB_FREQ_FACTOR) * HEAD_BOB_AMPLITUDE;
    }
  }

  _trySpawn(playerPos) {
    const angle = Math.random() * Math.PI * 2;
    const dist = SPAWN_DIST_MIN + Math.random() * SPAWN_DIST_RANGE;
    const wx = Math.floor(playerPos.x + Math.cos(angle) * dist);
    const wz = Math.floor(playerPos.z + Math.sin(angle) * dist);
    const wy = this.world.getTopBlock(wx, wz);
    if (wy <= 0) return;

    const surfaceBlock = this.world.getBlock(wx, wy, wz);
    if (surfaceBlock === BlockId.WATER || surfaceBlock === BlockId.AIR) return;

    // Check proximity to villas for context-aware spawning
    const nearMiseria = this.villaMiseriaPos && Math.hypot(wx - this.villaMiseriaPos.x, wz - this.villaMiseriaPos.z) < VILLA_PROXIMITY_RADIUS;
    const nearProsperidad = this.villaProsperidadPos && Math.hypot(wx - this.villaProsperidadPos.x, wz - this.villaProsperidadPos.z) < VILLA_PROXIMITY_RADIUS;
    const nearCiudad = this.ciudadPos && Math.hypot(wx - this.ciudadPos.x, wz - this.ciudadPos.z) < VILLA_PROXIMITY_RADIUS;

    let type, variant = null;
    let professionForced = null;

    if (nearCiudad) {
      // Ciudad: professional villagers + steve/alex
      const pool = ['VILLAGER', 'VILLAGER', 'VILLAGER', 'VILLAGER', 'STEVE', 'ALEX', 'CAT'];
      type = pool[Math.floor(Math.random() * pool.length)];
      variant = 'rich';
      // Always assign a profession when in Ciudad
      const profKeys = Object.keys(PROFESSIONS);
      professionForced = profKeys[Math.floor(Math.random() * profKeys.length)];
    } else if (nearMiseria) {
      // Villa Miseria: mostly poor villagers and thin animals
      const pool = ['VILLAGER', 'VILLAGER', 'VILLAGER', 'PIG', 'CHICKEN', 'COW', 'CAT'];
      type = pool[Math.floor(Math.random() * pool.length)];
      variant = 'poor';
    } else if (nearProsperidad) {
      // Villa Prosperidad: rich villagers, healthy animals, Steve/Alex
      const pool = ['VILLAGER', 'VILLAGER', 'VILLAGER', 'COW', 'SHEEP', 'STEVE', 'ALEX', 'CAT'];
      type = pool[Math.floor(Math.random() * pool.length)];
      variant = 'rich';
      // 50% chance a rich villager has a profession
      if (Math.random() < 0.5) {
        const profKeys = Object.keys(PROFESSIONS);
        professionForced = profKeys[Math.floor(Math.random() * profKeys.length)];
      }
    } else {
      const types = [
        'PIG', 'SHEEP', 'CHICKEN', 'COW',
        'PIG', 'SHEEP', 'CHICKEN', 'COW',
        'VILLAGER', 'VILLAGER',
        'STEVE', 'ALEX',
        'WOLF',
        'HORSE', 'HORSE', 'HORSE',
        'BEE',
      ];
      type = types[Math.floor(Math.random() * types.length)];
    }

    const entity = new Entity(type, wx + 0.5, wy + 1, wz + 0.5);
    entity.variant = variant;

    // Assign education to villagers
    if (type === 'VILLAGER') {
      if (variant === 'poor') {
        entity.education = POOR_EDUCATION[Math.floor(Math.random() * POOR_EDUCATION.length)];
      } else if (variant === 'rich') {
        entity.education = RICH_EDUCATION[Math.floor(Math.random() * RICH_EDUCATION.length)];
      } else {
        entity.education = EDUCATION_LEVELS[Math.floor(Math.random() * EDUCATION_LEVELS.length)];
      }
      // Profession overrides the name tag (set above based on locale)
      if (professionForced) {
        entity.profession = professionForced;
      }
    }

    this._createMesh(entity);
    this.entities.push(entity);
  }

  _spawnHerobrine(playerPos) {
    // Spawn Herobrine at the edge of view distance, standing still, staring at the player
    const angle = Math.random() * Math.PI * 2;
    const dist = HEROBRINE_SPAWN_DIST_BASE + Math.random() * HEROBRINE_SPAWN_DIST_RANGE;
    const wx = Math.floor(playerPos.x + Math.cos(angle) * dist);
    const wz = Math.floor(playerPos.z + Math.sin(angle) * dist);
    const wy = this.world.getTopBlock(wx, wz);
    if (wy <= 0) return;

    const surfaceBlock = this.world.getBlock(wx, wy, wz);
    if (surfaceBlock === BlockId.WATER) return;

    const entity = new Entity('HEROBRINE', wx + 0.5, wy + 1, wz + 0.5);
    entity.soundTimer = HEROBRINE_INITIAL_SOUND_DELAY;
    entity.herobrineLifetime = HEROBRINE_LIFETIME_BASE + Math.random() * HEROBRINE_LIFETIME_RANGE;
    entity.tpCooldown = 4.0 + Math.random() * 2.0;
    entity.torchTimer = 5.0 + Math.random() * 3.0;
    this._createMesh(entity);
    this.entities.push(entity);
    this.herobrineActive = true;

    // Spawn lightning bolt visual at his location
    this._spawnLightningBolt(entity.position.x, entity.position.y, entity.position.z);

    // Play creepy ambient sound
    if (this.soundManager) {
      this.soundManager.playHerobrineAmbient(playerPos, entity.position);
    }
  }

  _spawnLightningBolt(x, y, z) {
    // Single tall emissive bolt that fades out over ~0.6s
    const boltGeo = new THREE.BoxGeometry(0.4, 18, 0.4);
    const boltMat = new THREE.MeshBasicMaterial({
      color: 0xCCDDFF, transparent: true, opacity: 1.0,
    });
    const bolt = new THREE.Mesh(boltGeo, boltMat);
    bolt.position.set(x, y + 8, z);
    this.scene.add(bolt);

    // Halo (wider, more transparent)
    const haloGeo = new THREE.BoxGeometry(1.2, 18, 1.2);
    const haloMat = new THREE.MeshBasicMaterial({
      color: 0xAACCFF, transparent: true, opacity: 0.4,
    });
    const halo = new THREE.Mesh(haloGeo, haloMat);
    halo.position.copy(bolt.position);
    this.scene.add(halo);

    // Add to a list we tick down each frame
    if (!this._lightningBolts) this._lightningBolts = [];
    this._lightningBolts.push({
      mesh: bolt, halo, ttl: 0.6, maxTtl: 0.6,
    });
  }

  _updateHerobrine(entity, dt, playerPos, index) {
    const dist = entity.position.distanceTo(playerPos);

    // Always face the player (creepy stare)
    if (entity.mesh) {
      const dx = playerPos.x - entity.position.x;
      const dz = playerPos.z - entity.position.z;
      entity.mesh.rotation.y = Math.atan2(dx, dz);
      entity.mesh.position.set(entity.position.x, entity.position.y, entity.position.z);
    }
    this._updateBlobShadow(entity);

    entity.herobrineLifetime -= dt;

    // Disappear if player gets too close or lifetime expired
    if (dist < HEROBRINE_VANISH_DISTANCE || entity.herobrineLifetime <= 0) {
      // Vanish effect - play sound
      if (this.soundManager) {
        this.soundManager.playHerobrineVanish(playerPos, entity.position);
      }
      this._removeEntity(index);
      this.herobrineActive = false;
      return;
    }

    // Despawn if too far
    if (dist > HEROBRINE_DESPAWN_DISTANCE) {
      this._removeEntity(index);
      this.herobrineActive = false;
      return;
    }

    // Eerie ambient sound periodically
    entity.soundTimer -= dt;
    if (entity.soundTimer <= 0 && this.soundManager) {
      this.soundManager.playHerobrineAmbient(playerPos, entity.position);
      entity.soundTimer = HEROBRINE_SOUND_BASE + Math.random() * HEROBRINE_SOUND_RANGE;
    }

    // POWER 1: Teleport behind the player every few seconds
    entity.tpCooldown = (entity.tpCooldown ?? 4) - dt;
    if (entity.tpCooldown <= 0 && dist > 18) {
      // Pick a spot ~12 blocks behind the player (random angle behind)
      const behindAngle = Math.atan2(entity.position.z - playerPos.z, entity.position.x - playerPos.x);
      const tpAngle = behindAngle + (Math.random() - 0.5) * 0.6;
      const tpDist = 14 + Math.random() * 4;
      const tx = Math.floor(playerPos.x + Math.cos(tpAngle) * tpDist);
      const tz = Math.floor(playerPos.z + Math.sin(tpAngle) * tpDist);
      const ty = this.world.getTopBlock(tx, tz);
      if (ty > 0) {
        const surface = this.world.getBlock(tx, ty, tz);
        if (surface !== BlockId.WATER) {
          entity.position.set(tx + 0.5, ty + 1, tz + 0.5);
          if (entity.mesh) entity.mesh.position.copy(entity.position);
          this._spawnLightningBolt(entity.position.x, entity.position.y, entity.position.z);
        }
      }
      entity.tpCooldown = 4.0 + Math.random() * 3.0;
    }

    // POWER 2: Plant a torch on a small pillar to mark territory
    entity.torchTimer = (entity.torchTimer ?? 5) - dt;
    if (entity.torchTimer <= 0) {
      const tx = Math.floor(entity.position.x);
      const tz = Math.floor(entity.position.z);
      const ty = this.world.getTopBlock(tx, tz);
      if (ty > 0 && ty < 250) {
        // Stack 1-3 dirt blocks + torch on top
        const stackH = 1 + Math.floor(Math.random() * 3);
        for (let i = 1; i <= stackH; i++) {
          if (this.world.getBlock(tx, ty + i, tz) === BlockId.AIR) {
            this.world.setBlock(tx, ty + i, tz, BlockId.DIRT);
          }
        }
        if (BlockId.TORCH !== undefined && this.world.getBlock(tx, ty + stackH + 1, tz) === BlockId.AIR) {
          this.world.setBlock(tx, ty + stackH + 1, tz, BlockId.TORCH);
        }
      }
      entity.torchTimer = 6.0 + Math.random() * 4.0;
    }
  }

  _updateLightningBolts(dt) {
    if (!this._lightningBolts) return;
    for (let i = this._lightningBolts.length - 1; i >= 0; i--) {
      const b = this._lightningBolts[i];
      b.ttl -= dt;
      const t = Math.max(0, b.ttl / b.maxTtl);
      if (b.mesh.material) b.mesh.material.opacity = t;
      if (b.halo.material) b.halo.material.opacity = t * 0.4;
      if (b.ttl <= 0) {
        this.scene.remove(b.mesh);
        this.scene.remove(b.halo);
        if (b.mesh.geometry) b.mesh.geometry.dispose();
        if (b.halo.geometry) b.halo.geometry.dispose();
        if (b.mesh.material) b.mesh.material.dispose();
        if (b.halo.material) b.halo.material.dispose();
        this._lightningBolts.splice(i, 1);
      }
    }
  }

  /**
   * Public method to spawn an entity at a specific position.
   * Used by terminal /summon command.
   */
  spawnEntity(type, x, y, z) {
    if (this.entities.length >= this.maxEntities) return;
    const entity = new Entity(type, x, y, z);
    this._createMesh(entity);
    this.entities.push(entity);
    return entity;
  }

  /**
   * Create a radial gradient texture for blob shadows.
   * Dark center, transparent edges.
   */
  _createShadowTexture() {
    const size = 64;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    const gradient = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
    gradient.addColorStop(0, 'rgba(0, 0, 0, 0.35)');
    gradient.addColorStop(0.5, 'rgba(0, 0, 0, 0.2)');
    gradient.addColorStop(1, 'rgba(0, 0, 0, 0.0)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, size, size);
    const tex = new THREE.CanvasTexture(canvas);
    tex.needsUpdate = true;
    return tex;
  }

  /**
   * Create a blob shadow mesh for an entity.
   */
  _createBlobShadow(entity) {
    const mat = new THREE.MeshBasicMaterial({
      map: this._shadowTexture,
      transparent: true,
      depthWrite: false,
      fog: false,
    });
    const shadow = new THREE.Mesh(this._shadowGeo, mat);
    shadow.rotation.x = -Math.PI / 2; // lay flat on ground
    shadow.renderOrder = 0;
    this.scene.add(shadow);
    entity.shadow = shadow;
  }

  /**
   * Update blob shadow position: follows entity X/Z, sits on ground level.
   */
  _updateBlobShadow(entity) {
    if (!entity.shadow) return;
    const ex = entity.position.x;
    const ez = entity.position.z;
    // Find ground level below entity
    const groundY = this.world.getTopBlock(Math.floor(ex), Math.floor(ez));
    const shadowY = (groundY > 0 ? groundY + 1.02 : entity.position.y - 0.5);
    entity.shadow.position.set(ex, shadowY, ez);

    // Scale shadow based on height above ground — smaller when higher
    const heightAbove = Math.max(0, entity.position.y - shadowY);
    const scale = Math.max(0.3, 1.0 - heightAbove * 0.05);
    entity.shadow.scale.set(scale, scale, 1);

    // Fade shadow when entity is very high
    entity.shadow.material.opacity = Math.max(0.1, 1.0 - heightAbove * 0.04);
  }

  _createMesh(entity) {
    const mob = MOB_TYPES[entity.type];
    if (!mob) return;
    const group = new THREE.Group();
    const refs = mob.build(group, entity.variant, entity.profession);
    if (refs) {
      entity.legFL = refs.legFL;
      entity.legFR = refs.legFR;
      entity.legBL = refs.legBL;
      entity.legBR = refs.legBR;
      entity.headMesh = refs.headMesh;
      entity.armL = refs.armL || null;
      entity.armR = refs.armR || null;
    }

    // Name tag for villagers — profession label if any, otherwise education
    if (entity.profession || entity.education) {
      const profData = entity.profession ? PROFESSIONS[entity.profession] : null;
      const tagText = profData ? profData.label : entity.education;
      const tag = this._createNameTag(tagText, entity.variant, !!profData);
      const S = PX * 1.8;
      tag.position.set(0, 42 * S, 0);
      group.add(tag);
      entity.nameTag = tag;
    }

    this.scene.add(group);
    entity.mesh = group;

    // Add blob shadow
    this._createBlobShadow(entity);
  }

  _createNameTag(text, variant, isProfession = false) {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = 256;
    canvas.height = 64;

    // Background
    ctx.fillStyle = isProfession ? 'rgba(20,40,80,0.75)' : 'rgba(0,0,0,0.6)';
    ctx.fillRect(0, 8, 256, 48);

    // Text
    ctx.font = 'bold 24px monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    if (isProfession) {
      ctx.fillStyle = '#7AD8FF';
    } else if (variant === 'rich') {
      ctx.fillStyle = '#FFD700';
    } else if (variant === 'poor') {
      ctx.fillStyle = '#AA8866';
    } else {
      ctx.fillStyle = '#FFFFFF';
    }
    ctx.fillText(text, 128, 32);

    const texture = new THREE.CanvasTexture(canvas);
    texture.minFilter = THREE.LinearFilter;
    const mat = new THREE.SpriteMaterial({ map: texture, transparent: true, depthTest: false });
    const sprite = new THREE.Sprite(mat);
    sprite.scale.set(1.5, 0.4, 1);
    return sprite;
  }

  _updateAI(entity, dt) {
    const mob = MOB_TYPES[entity.type];
    if (!mob) return;
    if (mob.isHerobrine) return; // Herobrine has its own AI

    // Wolf-specific AI
    if (mob.isWolf) {
      this._updateWolfAI(entity, dt);
      return;
    }

    // Tamed cat follows player (like wolf)
    if (mob.isCat && entity.tamed && this._playerRef) {
      const dx = this._playerRef.x - entity.position.x;
      const dz = this._playerRef.z - entity.position.z;
      const dist = Math.sqrt(dx * dx + dz * dz);
      if (dist > 5) {
        entity.velocity.x = (dx / dist) * mob.speed;
        entity.velocity.z = (dz / dist) * mob.speed;
        if (entity.mesh) entity.mesh.rotation.y = Math.atan2(dx, dz);
      } else if (dist > 2) {
        entity.velocity.x = (dx / dist) * mob.speed * 0.3;
        entity.velocity.z = (dz / dist) * mob.speed * 0.3;
      } else {
        entity.velocity.x = 0;
        entity.velocity.z = 0;
      }
      if (dist > 30) {
        entity.position.set(this._playerRef.x + (Math.random() - 0.5) * 3, this._playerRef.y, this._playerRef.z + (Math.random() - 0.5) * 3);
      }
      return;
    }

    entity.idleTimer -= dt;
    if (entity.idleTimer > 0) {
      entity.velocity.x = 0;
      entity.velocity.z = 0;
      return;
    }

    entity.wanderTimer -= dt;
    if (entity.wanderTimer <= 0) {
      if (Math.random() < IDLE_CHANCE) {
        entity.idleTimer = IDLE_TIME_BASE + Math.random() * IDLE_TIME_RANGE;
        entity.velocity.x = 0;
        entity.velocity.z = 0;
      } else {
        let angle;
        if (mob.isVillager || mob.isHumanoid) {
          // Villagers and human NPCs stay near home
          // Use temp vector instead of clone()
          const toHome = _tempVec3A.subVectors(entity.homePosition, entity.position);
          toHome.y = 0;
          if (toHome.length() > VILLAGER_HOME_RADIUS) {
            angle = Math.atan2(toHome.x, toHome.z) + (Math.random() - 0.5) * 1.0;
          } else {
            angle = Math.random() * Math.PI * 2;
          }
        } else {
          angle = Math.random() * Math.PI * 2;
        }
        entity.wanderDir.set(Math.sin(angle), 0, Math.cos(angle));
        entity.wanderTimer = WANDER_TIME_BASE + Math.random() * WANDER_TIME_RANGE;
      }
    }

    // Avoid lava/water: check block ahead before moving
    const aheadX = Math.floor(entity.position.x + entity.wanderDir.x * 1.5);
    const aheadZ = Math.floor(entity.position.z + entity.wanderDir.z * 1.5);
    const aheadY = Math.floor(entity.position.y);
    if (this.world) {
      const blockAhead = this.world.getBlock(aheadX, aheadY, aheadZ);
      const blockBelow = this.world.getBlock(aheadX, aheadY - 1, aheadZ);
      // Avoid water (8,9), lava (10,11), and cliff edges (air below)
      if (blockAhead === 8 || blockAhead === 9 || blockAhead === 10 || blockAhead === 11 ||
          blockBelow === 0 || blockBelow === 8 || blockBelow === 10) {
        // Turn around
        entity.wanderDir.x = -entity.wanderDir.x;
        entity.wanderDir.z = -entity.wanderDir.z;
        entity.wanderTimer = 0.5;
      }
    }

    entity.velocity.x = entity.wanderDir.x * mob.speed;
    entity.velocity.z = entity.wanderDir.z * mob.speed;

    if (entity.mesh && (entity.velocity.x !== 0 || entity.velocity.z !== 0)) {
      entity.mesh.rotation.y = Math.atan2(entity.velocity.x, entity.velocity.z);
    }
  }

  _updateWolfAI(entity, dt) {
    const speed = MOB_TYPES.WOLF.speed;

    // Tick attack cooldown
    if (entity.wolfAttackCooldown > 0) {
      entity.wolfAttackCooldown -= dt;
    }

    // --- Tamed wolf: follow the player ---
    if (entity.tamed && this._lastPlayerPos) {
      // Use pooled temp vector instead of allocating new one
      const toPlayer = _tempVec3A.subVectors(this._lastPlayerPos, entity.position);
      toPlayer.y = 0;
      const distToPlayer = toPlayer.length();

      // If too far from player, teleport nearby
      if (distToPlayer > WOLF_TELEPORT_DISTANCE) {
        const angle = Math.random() * Math.PI * 2;
        const wx = this._lastPlayerPos.x + Math.cos(angle) * WOLF_TELEPORT_OFFSET;
        const wz = this._lastPlayerPos.z + Math.sin(angle) * WOLF_TELEPORT_OFFSET;
        entity.position.set(
          Math.max(-1000, Math.min(1000, wx)),
          this._lastPlayerPos.y,
          Math.max(-1000, Math.min(1000, wz))
        );
        if (entity.mesh) entity.mesh.position.copy(entity.position);
        return;
      }

      // Follow player if farther than follow distance
      if (distToPlayer > WOLF_FOLLOW_DISTANCE) {
        toPlayer.normalize();
        entity.velocity.x = toPlayer.x * speed;
        entity.velocity.z = toPlayer.z * speed;
        if (entity.mesh) {
          entity.mesh.rotation.y = Math.atan2(toPlayer.x, toPlayer.z);
        }
      } else {
        // Close enough, idle
        entity.velocity.x = 0;
        entity.velocity.z = 0;
      }

      // Attack what player attacks (wolfTarget set externally from main.js)
      if (entity.wolfTarget) {
        // Use pooled temp vector B (A is in use for toPlayer)
        const toTarget = _tempVec3B.subVectors(entity.wolfTarget, entity.position);
        toTarget.y = 0;
        const targetDist = toTarget.length();

        if (targetDist > 1.5) {
          toTarget.normalize();
          entity.velocity.x = toTarget.x * speed;
          entity.velocity.z = toTarget.z * speed;
          if (entity.mesh) {
            entity.mesh.rotation.y = Math.atan2(toTarget.x, toTarget.z);
          }
        }
      }

      return;
    }

    // --- Wild hostile wolf (after being attacked) ---
    if (entity.wolfHostile && this._lastPlayerPos) {
      // Use pooled temp vector instead of allocating new one
      const toPlayer = _tempVec3A.subVectors(this._lastPlayerPos, entity.position);
      toPlayer.y = 0;
      const distToPlayer = toPlayer.length();
      toPlayer.normalize();

      entity.velocity.x = toPlayer.x * speed;
      entity.velocity.z = toPlayer.z * speed;

      if (entity.mesh) {
        entity.mesh.rotation.y = Math.atan2(toPlayer.x, toPlayer.z);
      }

      // Lose aggro if player is very far
      if (distToPlayer > WOLF_AGGRO_LOSS_DISTANCE) {
        entity.wolfHostile = false;
      }

      return;
    }

    // --- Wild passive wolf: standard wander AI ---
    entity.idleTimer -= dt;
    if (entity.idleTimer > 0) {
      entity.velocity.x = 0;
      entity.velocity.z = 0;
      return;
    }

    entity.wanderTimer -= dt;
    if (entity.wanderTimer <= 0) {
      if (Math.random() < IDLE_CHANCE) {
        entity.idleTimer = IDLE_TIME_BASE + Math.random() * IDLE_TIME_RANGE;
        entity.velocity.x = 0;
        entity.velocity.z = 0;
      } else {
        const angle = Math.random() * Math.PI * 2;
        entity.wanderDir.set(Math.sin(angle), 0, Math.cos(angle));
        entity.wanderTimer = WANDER_TIME_BASE + Math.random() * WANDER_TIME_RANGE;
      }
    }

    entity.velocity.x = entity.wanderDir.x * speed * WOLF_WILD_SPEED_FACTOR;
    entity.velocity.z = entity.wanderDir.z * speed * WOLF_WILD_SPEED_FACTOR;

    if (entity.mesh && (entity.velocity.x !== 0 || entity.velocity.z !== 0)) {
      entity.mesh.rotation.y = Math.atan2(entity.velocity.x, entity.velocity.z);
    }
  }

  _applyPhysics(entity, dt) {
    entity.velocity.y -= Math.abs(GRAVITY) * dt;
    if (entity.velocity.y < TERMINAL_VELOCITY) entity.velocity.y = TERMINAL_VELOCITY;

    const newX = entity.position.x + entity.velocity.x * dt;
    const newY = entity.position.y + entity.velocity.y * dt;
    const newZ = entity.position.z + entity.velocity.z * dt;

    const gx = Math.floor(newX);
    const gy = Math.floor(newY - GROUND_CHECK_OFFSET);
    const gz = Math.floor(newZ);
    const blockBelow = this.world.getBlock(gx, gy, gz);

    if (blockBelow !== BlockId.AIR && blockBelow !== BlockId.WATER) {
      entity.position.y = gy + 1;
      entity.velocity.y = 0;
    } else {
      entity.position.y = newY;
    }

    const cx = Math.floor(newX);
    const cz = Math.floor(newZ);
    const cy = Math.floor(entity.position.y);
    const blockAtPos = this.world.getBlock(cx, cy, cz);

    if (blockAtPos !== BlockId.AIR && blockAtPos !== BlockId.WATER) {
      entity.wanderDir.multiplyScalar(-1);
      entity.velocity.x *= -1;
      entity.velocity.z *= -1;
      entity.wanderTimer = 0;
    } else {
      entity.position.x = newX;
      entity.position.z = newZ;
    }

    if (entity.position.y < WORLD_BOTTOM) entity.position.y = WORLD_BOTTOM;
    if (entity.position.y > 128) entity.position.y = 128;
  }

  _removeEntity(index) {
    const entity = this.entities[index];
    if (entity.mesh) {
      this.scene.remove(entity.mesh);
      entity.mesh.traverse(child => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) {
          if (child.material.map) child.material.map.dispose();
          if (Array.isArray(child.material)) {
            child.material.forEach(m => {
              if (m.map) m.map.dispose();
              m.dispose();
            });
          } else {
            child.material.dispose();
          }
        }
      });
    }
    // Remove blob shadow
    if (entity.shadow) {
      this.scene.remove(entity.shadow);
      entity.shadow.material.dispose();
      entity.shadow = null;
    }
    this.entities.splice(index, 1);
  }

  /**
   * Attempt to tame a wolf with a bone. 33% chance of success.
   * @param {Entity} entity - The wolf entity
   * @returns {boolean} true if taming succeeded
   */
  tryTameWolf(entity) {
    if (entity.type !== 'WOLF' || entity.tamed) return false;

    if (Math.random() < WOLF_TAME_CHANCE) {
      // Taming success!
      entity.tamed = true;
      entity.owner = 'player';
      entity.wolfHostile = false;
      entity.health = WOLF_TAMED_HEALTH;
      entity.maxHealth = WOLF_TAMED_HEALTH;

      // Show collar
      if (entity.mesh) {
        entity.mesh.traverse(child => {
          if (child.name === 'wolfCollar') {
            child.visible = true;
          }
        });
      }
      return true;
    }
    return false;
  }

  /**
   * Called when a wolf is attacked by the player. Makes wild wolves hostile.
   * @param {Entity} entity - The wolf entity
   */
  onWolfAttacked(entity) {
    if (entity.type !== 'WOLF') return;
    if (entity.tamed) return; // Tamed wolves don't retaliate
    entity.wolfHostile = true;
  }

  /**
   * Get the wolf attack damage (used by main.js for hostile wolf melee)
   * @param {Entity} entity
   * @returns {number} damage amount
   */
  getWolfAttackDamage(entity) {
    if (entity.type !== 'WOLF') return 0;
    if (!entity.wolfHostile && !entity.tamed) return 0;
    return WOLF_ATTACK_DAMAGE;
  }

  /**
   * Check if a hostile wolf is in melee range and ready to attack.
   * @param {Entity} entity
   * @param {THREE.Vector3} playerPos
   * @returns {{ canAttack: boolean, damage: number }}
   */
  checkWolfAttack(entity, playerPos) {
    if (entity.type !== 'WOLF') return { canAttack: false, damage: 0 };
    if (!entity.wolfHostile) return { canAttack: false, damage: 0 };
    if (entity.wolfAttackCooldown > 0) return { canAttack: false, damage: 0 };

    const dist = entity.position.distanceTo(playerPos);
    if (dist <= WOLF_MELEE_RANGE) {
      entity.wolfAttackCooldown = WOLF_ATTACK_COOLDOWN;
      return { canAttack: true, damage: WOLF_ATTACK_DAMAGE };
    }
    return { canAttack: false, damage: 0 };
  }

  dispose() {
    for (let i = this.entities.length - 1; i >= 0; i--) {
      this._removeEntity(i);
    }
    // Clean up shared shadow resources
    if (this._shadowTexture) {
      this._shadowTexture.dispose();
    }
    if (this._shadowGeo) {
      this._shadowGeo.dispose();
    }
  }
}
